import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, signInWithSpotify, signOut as supabaseSignOut } from "./supabase";

// ─── useIsMobile ──────────────────────────────────────────────────────────────
// Detects whether the viewport is in mobile range (<= 768px wide). Re-evaluates
// on resize / orientation change. Used to bump up font + image sizes in
// touch-heavy areas (Figjam Player track list, ride builder, etc.).
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, [breakpoint]);
  return isMobile;
}

// ─── Service Config ────────────────────────────────────────────────────────────
const SVC = {
  spotify: { label: "Spotify", color: "#1DB954", bg: "linear-gradient(135deg,#071a0e 0%,#0f3d1f 100%)" },
  strava:  { label: "Strava",  color: "#FC4C02", bg: "linear-gradient(135deg,#200a00 0%,#3d1500 100%)" },
};

const SPOTIFY_TYPES = [
  { key: "Now Playing", label: "Playing",   icon: "🎵" },
  { key: "Played",      label: "Played",    icon: "🎧" },
  { key: "Liked",       label: "Liked",     icon: "❤️" },
  { key: "Playlist",    label: "Playlists", icon: "📋" },
  { key: "Top Artist",  label: "Top Artists", icon: "⭐" },
  { key: "Top Track",   label: "Top Tracks", icon: "🔥" },
  { key: "Followed Artist", label: "Following", icon: "👤" },
  { key: "New Release", label: "Releases",  icon: "💿" },
];

// ─── Ride builder presets ─────────────────────────────────────────────────────
// Hoisted to module scope so both the RidePlaylistBuilder (selects via these)
// and the player tile (renders the matched label as a pill) can reference the
// same source of truth. matchPresetLabel finds the preset whose prompt string
// matches the user's notes verbatim — used to attach a friendly label to the
// saved playlist's build context.
const FIGJAM_BUILDER_PRESETS = [
  {
    label: "🤘 Metal climbs, chill descents",
    prompt: "Heavy metal and hard rock (Tool, Metallica, Rage Against the Machine style) on climbs — aggressive and driving. Mellow jam-band and psychedelic music (Phish, Grateful Dead, Widespread Panic vibes) on descents. Steady indie rock on flats. Avoid slow ballads.",
  },
  {
    label: "⚡ All energy, all ride",
    prompt: "GENRE CONSTRAINT: Only pick tracks that are clearly electronic music — EDM, house, techno, trance, drum & bass, progressive electronic, dubstep, or electronic dance. Do NOT include rock, hip-hop, indie, jam band, folk, acoustic, or any non-electronic genre, even if they're high energy. If a track isn't unambiguously electronic, skip it. If only a few electronic songs exist in the library, use fewer tracks rather than substituting other genres. Big euphoric drops on climbs. Four-on-the-floor driving beats on flats. Uplifting progressive on descents.",
  },
  {
    label: "🎸 Classic rock anthems",
    prompt: "Classic rock from the 70s and 80s — Zeppelin, AC/DC, Springsteen, Van Halen energy. Arena-rock anthems with big drums and guitar solos on climbs. Bluesy mid-tempo rock on flats. Upbeat Stones-style rock on descents. Nothing modern, nothing mellow.",
  },
  {
    label: "🌈 Jamband groove",
    prompt: "STRICT GENRE LOCK: ONLY jam band, psychedelic rock, jam-grass, bluegrass-jam, and improvisational music. Include artists like Goose, Grateful Dead, Widespread Panic, Phish, Joe Russo's Almost Dead, String Cheese Incident, moe., Umphrey's McGee, Trey Anastasio Band, Dead & Company, Leftover Salmon, Billy Strings, Greensky Bluegrass, The Disco Biscuits, Lotus, Spafford, Pigeons Playing Ping Pong, Vulfpeck, Lettuce, Snarky Puppy, Khruangbin, and similar jam scene artists. STRICTLY REJECT (do not pick even if energy fits): metal, alt-metal, nu-metal, industrial, hard rock, hip-hop, EDM, pop, pop-punk, punk, emo, country (non-bluegrass), R&B, mainstream rock. Specifically reject artists like Tool, Metallica, Linkin Park, Nine Inch Nails, Rage Against the Machine, AC/DC, Led Zeppelin, Foo Fighters, Pearl Jam, Soundgarden — none of these are jam band. High-energy peak-jam moments on climbs (full-band improv, fast bluegrass-jam, Phish Type II jams). Mid-tempo classic groove on flats (Dead, Widespread Panic, JRAD). Floating psychedelic + slower jams on descents (mellow Phish, Yonder Mountain). If the library lacks enough jam-band tracks, return a SHORTER playlist — never substitute off-genre tracks.",
  },
  {
    label: "🌅 Chill downtempo",
    prompt: "GENRE CONSTRAINT: Downtempo, chillout, ambient electronic, and easy-listening instrumental. Include artists like Tycho, 16 Bit Lolitas, Moby, Nightmares on Wax, Boards of Canada, Bonobo, Thievery Corporation, Emancipator, Air, Massive Attack, Portishead, Zero 7, Röyksopp (chill side), DJ Shadow, Four Tet, RJD2, ODESZA (chill side), Bibio, Helios, Ulrich Schnauss, Carbon Based Lifeforms, and similar atmospheric/downtempo electronic artists. The more upbeat / driving downtempo tracks (Tycho 'Awake', Bonobo 'Cirrus', Moby 'Porcelain' style) on climbs — still chill but with forward motion. Mid-tempo grooves (Nightmares on Wax, Thievery Corporation, Emancipator) on flats. Most ambient + floaty + spacious (Boards of Canada, Helios, Ulrich Schnauss, Tycho 'Dive' side) on descents. Avoid loud rock, metal, hip-hop with hard beats, mainstream pop, aggressive EDM. Keep it instrumental-leaning; vocals fine if they're hushed/ethereal.",
  },
];
function matchPresetLabel(notes) {
  if (!notes) return null;
  const trimmed = notes.trim();
  if (!trimmed) return null;
  const hit = FIGJAM_BUILDER_PRESETS.find(p => p.prompt === trimmed);
  return hit?.label || null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function ago(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

function dateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const day = d.toLocaleDateString("en-US", { weekday: "short" });
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${date} · ${time}`;
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function TruncatedText({ text, style, tooltipColor = "#1DB954" }) {
  const ref = useRef(null);
  const longPressTimer = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Detect if text is overflowing
  useEffect(() => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [text]);

  // Auto-dismiss tooltip on mobile after 3 seconds
  useEffect(() => {
    if (!showTooltip) return;
    const t = setTimeout(() => setShowTooltip(false), 3000);
    return () => clearTimeout(t);
  }, [showTooltip]);

  // Mobile long-press handlers
  const handleTouchStart = (e) => {
    if (!isTruncated) return;
    longPressTimer.current = setTimeout(() => {
      e.stopPropagation();
      setShowTooltip(true);
    }, 400);
  };
  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <div
      style={{ position: "relative", ...style, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      ref={ref}
      onMouseEnter={() => isTruncated && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {text}
      {showTooltip && isTruncated && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: 6,
            background: "#0f1419", border: `1px solid ${tooltipColor}40`,
            borderRadius: 8, padding: "8px 12px",
            fontSize: 13, fontWeight: 500, color: "#fff",
            fontFamily: "'DM Sans',sans-serif",
            whiteSpace: "normal", wordBreak: "break-word",
            zIndex: 50, maxWidth: 280, minWidth: 120,
            boxShadow: `0 6px 20px rgba(0,0,0,0.55)`,
            pointerEvents: "auto",
          }}
        >
          {text}
          <div style={{
            position: "absolute", top: -5, left: 16,
            width: 8, height: 8, transform: "rotate(45deg)",
            background: "#0f1419", borderTop: `1px solid ${tooltipColor}40`, borderLeft: `1px solid ${tooltipColor}40`,
          }} />
        </div>
      )}
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{
      background: `${color}18`, border: `1px solid ${color}35`,
      color, borderRadius: 20, padding: "3px 10px",
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: "6px 12px", textAlign: "center", flexShrink: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "monospace" }}>{value}</div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    </div>
  );
}

function SpotifyLink({ extra, color }) {
  if (!extra?.spotifyUri) return null;
  return (
    <a
      href={extra.spotifyUrl}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        window.location.href = extra.spotifyUri;
      }}
      title="Open in Spotify"
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 12, background: `${color}25`, color, fontSize: 11, fontWeight: 700, textDecoration: "none", flexShrink: 0, marginTop: 2 }}
    >
      ▶
    </a>
  );
}

function PlaylistBadge({ allPlaylists, color }) {
  const [hover, setHover] = useState(false);
  if (!allPlaylists || allPlaylists.length === 0) return null;
  const count = allPlaylists.length;
  return (
    <div
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={e => e.stopPropagation()}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: 20, height: 18, borderRadius: 9, padding: "0 5px",
        background: `${color}20`, border: `1px solid ${color}30`,
        color, fontSize: 10, fontWeight: 700, fontFamily: "monospace",
        cursor: "default", flexShrink: 0,
      }}>
        📋 {count}
      </span>
      {hover && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
          background: "#0f1419", border: `1px solid ${color}30`, borderRadius: 10, padding: "8px 12px",
          zIndex: 300, minWidth: 160, maxWidth: 260, boxShadow: `0 8px 24px rgba(0,0,0,0.6)`,
          pointerEvents: "auto",
        }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, fontFamily: "monospace" }}>
            In {count} playlist{count > 1 ? "s" : ""}
          </div>
          {allPlaylists.map((pl, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}>
              {pl.image && <img src={pl.image} alt="" style={{ width: 20, height: 20, borderRadius: 3, flexShrink: 0 }} />}
              <span style={{ fontSize: 12, color: "#fff", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pl.name}
              </span>
            </div>
          ))}
          {/* Arrow */}
          <div style={{ position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%) rotate(45deg)", width: 8, height: 8, background: "#0f1419", borderRight: `1px solid ${color}30`, borderBottom: `1px solid ${color}30` }} />
        </div>
      )}
    </div>
  );
}

function SpotifyEmbed({ uri }) {
  if (!uri) return null;
  // uri format: "spotify:track:ABC123" or "spotify:playlist:ABC123" etc.
  const parts = uri.split(":");
  if (parts.length < 3) return null;
  const type = parts[1]; // track, playlist, artist, album
  const id = parts[2];
  return (
    <div onClick={e => e.stopPropagation()} style={{ marginTop: 10, borderRadius: 12, overflow: "hidden" }}>
      <iframe
        title="Spotify Player"
        src={`https://open.spotify.com/embed/${type}/${id}?theme=0&utm_source=generator&autoplay=1`}
        width="100%"
        height={type === "track" ? 80 : 152}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        style={{ borderRadius: 12 }}
      />
    </div>
  );
}

function PlaylistExpander({ playlistId, trackUri, color, playlistTracks, setPlaylistTracks }) {
  const data = playlistTracks[playlistId];
  const [loading, setLoading] = useState(false);

  const fetchTracks = useCallback(async (offset, limit = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ playlistId, limit: String(limit) });
      if (trackUri && offset === undefined) params.set("trackUri", trackUri);
      if (offset !== undefined) params.set("offset", String(offset));
      const res = await fetch(`/api/playlist-tracks?${params}`);
      const d = await res.json();
      if (offset !== undefined && data) {
        // Merge with existing tracks
        const merged = [...(data.tracks || [])];
        d.tracks.forEach(t => {
          if (!merged.find(m => m.position === t.position)) merged.push(t);
        });
        merged.sort((a, b) => a.position - b.position);
        setPlaylistTracks(prev => ({ ...prev, [playlistId]: { ...d, tracks: merged } }));
      } else {
        setPlaylistTracks(prev => ({ ...prev, [playlistId]: d }));
      }
    } catch (e) { console.error("playlist fetch:", e); }
    setLoading(false);
  }, [playlistId, trackUri, data, setPlaylistTracks]);

  useEffect(() => {
    if (!data && !loading) fetchTracks();
  }, [data, loading, fetchTracks]);

  if (!data) return loading ? (
    <div style={{ display: "flex", alignItems: "stretch", marginBottom: 4 }}>
      <div style={{ width: 34, display: "flex", justifyContent: "center", flexShrink: 0 }}><div style={{ width: 2, background: `${color}20` }} /></div>
      <div style={{ flex: 1, marginLeft: 10, padding: "8px 0", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>Loading playlist…</div>
    </div>
  ) : null;

  const firstPos = data.tracks[0]?.position || 0;
  const lastPos = data.tracks[data.tracks.length - 1]?.position || 0;
  const canLoadBefore = firstPos > 0;
  const canLoadAfter = lastPos < data.total - 1;

  const fmtMs = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

  return (
    <div style={{ display: "flex", alignItems: "stretch", marginBottom: 4 }}>
      <div style={{ width: 34, display: "flex", justifyContent: "center", flexShrink: 0 }}>
        <div style={{ width: 2, background: `${color}20` }} />
      </div>
      <div style={{ flex: 1, marginLeft: 10, background: "rgba(0,0,0,0.25)", border: `1px solid ${color}15`, borderRadius: 10, overflow: "hidden" }}>
        {/* Load earlier */}
        {canLoadBefore && (
          <button
            onClick={() => fetchTracks(Math.max(0, firstPos - 5), 5)}
            disabled={loading}
            style={{ width: "100%", background: "none", border: "none", borderBottom: `1px solid ${color}10`, padding: "6px 12px", color, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
          >
            <span style={{ fontSize: 14 }}>+</span> {loading ? "Loading…" : `${firstPos} more above`}
          </button>
        )}

        {/* Track list */}
        {data.tracks.map(t => (
          <div
            key={t.position}
            onClick={() => { window.location.href = t.uri; }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", cursor: "pointer",
              background: t.isTarget ? `${color}12` : "transparent",
              borderLeft: t.isTarget ? `3px solid ${color}` : "3px solid transparent",
              transition: "background 0.15s",
            }}
            onMouseEnter={e => { if (!t.isTarget) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
            onMouseLeave={e => { if (!t.isTarget) e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "monospace", width: 18, textAlign: "right", flexShrink: 0 }}>{t.position + 1}</span>
            {t.albumArt && <img src={t.albumArt} alt="" style={{ width: 28, height: 28, borderRadius: 4, flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: t.isTarget ? 700 : 500, color: t.isTarget ? color : "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.artists}</div>
            </div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontFamily: "monospace", flexShrink: 0 }}>{fmtMs(t.duration)}</span>
          </div>
        ))}

        {/* Load more */}
        {canLoadAfter && (
          <button
            onClick={() => fetchTracks(lastPos + 1, 5)}
            disabled={loading}
            style={{ width: "100%", background: "none", border: "none", borderTop: `1px solid ${color}10`, padding: "6px 12px", color, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
          >
            <span style={{ fontSize: 14 }}>+</span> {loading ? "Loading…" : `${data.total - lastPos - 1} more below`}
          </button>
        )}
      </div>
    </div>
  );
}

function CardDetail({ item, color }) {
  const { service, type, extra } = item;
  if (!extra) return null;

  if (service === "spotify") {
    // ── Playlist detail ──
    if (type === "Playlist") return (
      <div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
          {extra.albumArt && <img src={extra.albumArt} alt="" style={{ width: 52, height: 52, borderRadius: 6, flexShrink: 0 }} />}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
            <Pill label={`${extra.trackCount} tracks`} color={color} />
            <Pill label={`by ${extra.owner}`} color={color} />
          </div>
        </div>
        <SpotifyEmbed uri={extra.spotifyUri} />
      </div>
    );

    // ── Artist detail (Top Artist / Followed Artist) ──
    if (type === "Top Artist" || type === "Followed Artist") return (
      <div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
          {extra.albumArt && <img src={extra.albumArt} alt="" style={{ width: 52, height: 52, borderRadius: "50%", flexShrink: 0 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              {extra.genres?.map(g => <Pill key={g} label={g} color={color} />)}
            </div>
            {extra.popularity !== undefined && <Pill label={`📈 ${extra.popularity}% popularity`} color={color} />}
          </div>
        </div>
        <SpotifyEmbed uri={extra.spotifyUri} />
      </div>
    );

    // ── New Release detail ──
    if (type === "New Release") return (
      <div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
          {extra.albumArt && <img src={extra.albumArt} alt="" style={{ width: 52, height: 52, borderRadius: 6, flexShrink: 0 }} />}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
            <Pill label={`${extra.trackCount} tracks`} color={color} />
            {extra.releaseDate && <Pill label={extra.releaseDate} color={color} />}
          </div>
        </div>
        <SpotifyEmbed uri={extra.spotifyUri} />
      </div>
    );

    // ── Track detail (Now Playing, Played, Liked, Top Track) ──
    return (
      <div>
        <SpotifyEmbed uri={extra.spotifyUri} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {extra.duration   && <Pill label={`⏱ ${extra.duration}`} color={color} />}
          {extra.isPlaying !== undefined && <Pill label={extra.isPlaying ? "▶ Playing" : "⏸ Paused"} color={color} />}
          {extra.popularity !== undefined && <Pill label={`📈 ${extra.popularity}%`} color={color} />}
        </div>
      </div>
    );
  }

  if (service === "strava") return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        {[
          { l: "Distance",  v: extra.distance  },
          { l: "Time",      v: extra.time      },
          { l: "Elevation", v: extra.elevation },
          extra.pace  && { l: "Pace",   v: extra.pace   },
          extra.avgHR && { l: "Avg HR", v: extra.avgHR  },
        ].filter(Boolean).map(s => <StatBox key={s.l} label={s.l} value={s.v} color={color} />)}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {extra.kudos > 0     && <Pill label={`👏 ${extra.kudos} kudos`} color={color} />}
        {extra.achievements  && <Pill label={extra.achievements}        color={color} />}
      </div>
    </div>
  );

  return null;
}

// ─── Connections Panel ────────────────────────────────────────────────────────
// ─── useFigjamPlayer ────────────────────────────────────────────────────────
// Shared Spotify Web Playback SDK lifecycle. Uses refs for deviceId/player so
// async callbacks don't hit stale state. One instance per app (use at root).
//
// Returns:
//   status: "idle" | "connecting" | "ready" | "playing" | "paused" | "error"
//   error:  string | null
//   track:  current playing track { name, artists, uri, albumArt } | null
//   queue:  current URI list being played (ordered)
//   queueIndex: current position in queue
//   initialize(): ensures player is ready — resolves when device_id is received
//   playQueue(tracks): plays an array of { uri, name, artists, albumArt } tracks
//   togglePlay(), nextTrack(), previousTrack(): playback controls
//   stop(): clears queue + disconnects
// Ensure Spotify plays the Figjam queue the way we want:
//   - shuffle OFF — preserves the AI's curated ride order
//   - repeat=context — when the last song ends, loops back to song #1
//     automatically (auto-restart the ride playlist)
// The Web Playback SDK inherits account-level shuffle/repeat state, so we
// enforce this on every play/jump. Both endpoints are idempotent; errors
// are swallowed (non-fatal).
async function forceLinearPlayback(deviceId, token) {
  try {
    await fetch(`https://api.spotify.com/v1/me/player/shuffle?state=false&device_id=${deviceId}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {}
  try {
    await fetch(`https://api.spotify.com/v1/me/player/repeat?state=context&device_id=${deviceId}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {}
}

const FIGJAM_LIBRARY_KEY = "figjam:library";
const FIGJAM_JAMS_KEY = "figjam:jams";           // URIs user marked "Add to my Jams" (thumbs up)
const FIGJAM_NOTJAM_KEY = "figjam:notJams";       // URIs user marked "Not my jam" (thumbs down)
const FIGJAM_DISCOVERED_KEY = "figjam:discovered"; // tracks discovered via search, full metadata
const FIGJAM_LEGACY_SAVED_KEY = "figjam:lastPlaylist"; // old single-entry key
const FIGJAM_DISMISSED_RESUME_KEY = "figjam:dismissedResumeId";
const FIGJAM_RESUME_POINT_KEY = "figjam:resumePoint"; // { uri, positionMs, savedAt }
const FIGJAM_LIBRARY_CAP = 4; // cap non-favorited entries — favorites are kept forever

// Load library from localStorage, migrating the old single-entry key if present
function loadLibrary() {
  try {
    const raw = localStorage.getItem(FIGJAM_LIBRARY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
    // Migrate legacy single-entry key → library array with one entry
    const legacy = localStorage.getItem(FIGJAM_LEGACY_SAVED_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (parsed?.tracks?.length) {
        const migrated = [{
          id: `pl_${new Date(parsed.savedAt || Date.now()).getTime()}`,
          name: parsed.name || "",
          tracks: parsed.tracks,
          savedAt: parsed.savedAt || new Date().toISOString(),
          favorited: false,
        }];
        localStorage.setItem(FIGJAM_LIBRARY_KEY, JSON.stringify(migrated));
        localStorage.removeItem(FIGJAM_LEGACY_SAVED_KEY);
        return migrated;
      }
    }
  } catch {}
  return [];
}

function useFigjamPlayer() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [track, setTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [playlistName, setPlaylistName] = useState("");
  const [volume, setVolumeState] = useState(0.7); // 0.0 – 1.0, matches SDK init
  const [library, setLibrary] = useState(loadLibrary);
  // Ref timestamp — when set to a future time, the queueIndex sync effect
  // ignores track.uri mismatches until the current moment passes it. Used
  // during manual jumps to prevent the sync effect from snapping the
  // selection back to the OLD track's index while Spotify is mid-switch.
  const manualJumpUntilRef = useRef(0);
  // Throttle the shuffle/repeat enforcement PUTs (see player_state_changed)
  const shuffleEnforcedAtRef = useRef(0);
  const repeatEnforcedAtRef = useRef(0);
  // Throttle the resume-point write — fires on every state change otherwise.
  const resumeSavedAtRef = useRef(0);
  // Thumbs-up set ("Add to my Jams") — URIs the user has saved to Spotify Liked.
  // We ALSO persist this locally so the UI can show the thumbs-up state
  // instantly without re-querying Spotify each render.
  const [jams, setJams] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FIGJAM_JAMS_KEY) || "[]")); } catch { return new Set(); }
  });
  // Thumbs-down set ("Not my jam") — URIs the user has rejected. Excluded
  // from future AI generations. Frontend-only; never sent to Spotify.
  const [notJams, setNotJams] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FIGJAM_NOTJAM_KEY) || "[]")); } catch { return new Set(); }
  });
  // Discovered-track pool — tracks Spotify Search returned that aren't in
  // the user's own library. Stored so future generations can reuse them.
  const [discoveredTracks, setDiscoveredTracks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FIGJAM_DISCOVERED_KEY) || "[]"); } catch { return []; }
  });
  const [dismissedResumeId, setDismissedResumeId] = useState(() => {
    try { return localStorage.getItem(FIGJAM_DISMISSED_RESUME_KEY) || null; } catch { return null; }
  });

  // Persist library whenever it changes
  useEffect(() => {
    try { localStorage.setItem(FIGJAM_LIBRARY_KEY, JSON.stringify(library)); } catch {}
  }, [library]);

  // ─── Phase B.4: cut library reads over to Postgres ─────────────────────
  // When the user signs in via Supabase, fetch their library from
  // /api/v2/playlists. If localStorage has entries NOT yet on the server
  // (first-time migration), push them up. Then replace local state with
  // the merged server view. localStorage continues to act as the offline
  // cache (writes still go there via the persist effect above).
  useEffect(() => {
    let cancelled = false;
    const syncLibraryFromServer = async (session) => {
      if (!session?.access_token) return;
      try {
        // 1. Fetch remote
        const res = await fetch("/api/v2/playlists", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) {
          console.warn("[figjam] library sync: GET failed", res.status);
          return;
        }
        const { playlists: remote } = await res.json();
        if (cancelled) return;

        // Map server → client schema
        const remoteMapped = (remote || []).map(p => ({
          id: p.id,
          name: p.name,
          tracks: p.tracks || [],
          savedAt: p.saved_at,
          favorited: !!p.favorited,
          activityContext: p.activity_context || undefined,
        }));

        // 2. One-time migration: push localStorage entries that AREN'T
        //    on the server up. Match by (name + first track URI) — same
        //    dedup key as the upsert helper.
        const remoteKeys = new Set(
          remoteMapped.map(p => `${p.name}::${p.tracks?.[0]?.uri || ""}`)
        );
        const local = (() => {
          try { return JSON.parse(localStorage.getItem(FIGJAM_LIBRARY_KEY) || "[]"); }
          catch { return []; }
        })();
        const toMigrate = local.filter(p => {
          const key = `${p.name}::${p.tracks?.[0]?.uri || ""}`;
          return p.tracks?.length && !remoteKeys.has(key);
        });
        if (toMigrate.length > 0) {
          console.log(`[figjam] library sync: migrating ${toMigrate.length} local entries to Postgres`);
          for (const entry of toMigrate) {
            try {
              await fetch("/api/v2/playlists", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                  name: entry.name,
                  tracks: entry.tracks,
                  favorited: !!entry.favorited,
                  activityContext: entry.activityContext || null,
                }),
              });
            } catch {}
          }
          // Refetch after migration
          const refetch = await fetch("/api/v2/playlists", {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (refetch.ok) {
            const { playlists: refreshed } = await refetch.json();
            const refreshedMapped = (refreshed || []).map(p => ({
              id: p.id, name: p.name, tracks: p.tracks || [],
              savedAt: p.saved_at, favorited: !!p.favorited,
              activityContext: p.activity_context || undefined,
            }));
            if (!cancelled) {
              setLibrary(refreshedMapped);
              console.log(`[figjam] library sync: ${refreshedMapped.length} entries (after migration)`);
            }
            return;
          }
        }

        // 3. Cut over: server is now the source of truth
        if (!cancelled) {
          setLibrary(remoteMapped);
          console.log(`[figjam] library sync: loaded ${remoteMapped.length} entries from Postgres`);
        }
      } catch (e) {
        console.warn("[figjam] library sync error:", e.message);
      }
    };

    // ─── Phase B.5: sync jams + not-jams + discovered ───────────────────────
    // Pull each from Postgres on sign-in. Locals get merged with remote
    // (server takes precedence; any local-only entries get pushed up).
    const syncJamsFromServer = async (session) => {
      if (!session?.access_token) return;
      try {
        // Jams
        const jamsRes = await fetch("/api/v2/jams", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (jamsRes.ok) {
          const { uris } = await jamsRes.json();
          if (!cancelled) {
            // Push any local-only jams up
            const local = (() => {
              try { return new Set(JSON.parse(localStorage.getItem(FIGJAM_JAMS_KEY) || "[]")); }
              catch { return new Set(); }
            })();
            const remoteSet = new Set(uris || []);
            for (const uri of local) {
              if (!remoteSet.has(uri)) {
                fetch("/api/v2/jams", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ uri }),
                }).catch(() => {});
                remoteSet.add(uri);
              }
            }
            setJams(remoteSet);
          }
        }
        // Not-jams
        const notRes = await fetch("/api/v2/not-jams", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (notRes.ok) {
          const { uris } = await notRes.json();
          if (!cancelled) {
            const local = (() => {
              try { return new Set(JSON.parse(localStorage.getItem(FIGJAM_NOTJAM_KEY) || "[]")); }
              catch { return new Set(); }
            })();
            const remoteSet = new Set(uris || []);
            for (const uri of local) {
              if (!remoteSet.has(uri)) {
                fetch("/api/v2/not-jams", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ uri }),
                }).catch(() => {});
                remoteSet.add(uri);
              }
            }
            setNotJams(remoteSet);
          }
        }
        // Discovered tracks — bulk migrate any local-only entries first
        const discRes = await fetch("/api/v2/discovered-tracks", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (discRes.ok) {
          const { tracks: remoteDisc } = await discRes.json();
          if (!cancelled) {
            const remoteUris = new Set((remoteDisc || []).map(t => t.uri));
            const local = (() => {
              try { return JSON.parse(localStorage.getItem(FIGJAM_DISCOVERED_KEY) || "[]"); }
              catch { return []; }
            })();
            const localOnly = (local || []).filter(t => t?.uri && !remoteUris.has(t.uri));
            if (localOnly.length > 0) {
              fetch("/api/v2/discovered-tracks", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                body: JSON.stringify({ tracks: localOnly }),
              }).catch(() => {});
              setDiscoveredTracks([...localOnly, ...(remoteDisc || [])].slice(0, 200));
            } else {
              setDiscoveredTracks(remoteDisc || []);
            }
          }
        }
        console.log("[figjam] jams/not-jams/discovered synced from Postgres");
      } catch (e) {
        console.warn("[figjam] jams sync error:", e.message);
      }
    };

    // Initial sync on mount (if already signed in)
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncLibraryFromServer(session);
      syncJamsFromServer(session);
    });
    // Re-sync on sign-in
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN") {
          syncLibraryFromServer(session);
          syncJamsFromServer(session);
        }
      }
    );
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  // Persist jams / not-jams / discovered tracks
  useEffect(() => {
    try { localStorage.setItem(FIGJAM_JAMS_KEY, JSON.stringify([...jams])); } catch {}
  }, [jams]);
  useEffect(() => {
    try { localStorage.setItem(FIGJAM_NOTJAM_KEY, JSON.stringify([...notJams])); } catch {}
  }, [notJams]);
  useEffect(() => {
    try { localStorage.setItem(FIGJAM_DISCOVERED_KEY, JSON.stringify(discoveredTracks)); } catch {}
  }, [discoveredTracks]);

  // ─── Thumbs up / "Add to my Jams" ──────────────────────────────────────────
  // Adds the currently-playing track to Figjam's local Jams pool so it's
  // remembered across sessions and included in future playlist generations.
  //
  // Note: This does NOT save to Spotify Liked. Spotify's Developer Mode
  // blocks PUT /v1/me/tracks with a blanket 403, regardless of OAuth scope.
  // Once the app is approved for Extended Quota Mode, we can re-enable the
  // Spotify sync. For now, the song lives in Figjam's pool instead.
  // Fire-and-forget helper — POST/DELETE to v2 endpoints if signed in.
  // Failure doesn't break the localStorage path; the next sign-in's sync
  // will re-converge the state.
  const v2Fetch = useCallback(async (path, method, body) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      await fetch(path, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {}
  }, []);

  const addToJams = useCallback(() => {
    if (!track?.uri) return;
    setJams(prev => new Set([...prev, track.uri]));
    setDiscoveredTracks(prev => {
      if (prev.some(t => t.uri === track.uri)) return prev;
      const entry = {
        id: track.uri.split(":").pop(),
        uri: track.uri,
        name: track.name,
        artists: track.artists,
        albumArt: track.albumArt,
        source: "jam",
      };
      // Dual-write the discovered entry too
      v2Fetch("/api/v2/discovered-tracks", "POST", { tracks: [entry] });
      return [entry, ...prev].slice(0, 200);
    });
    setNotJams(prev => {
      if (!prev.has(track.uri)) return prev;
      const next = new Set(prev); next.delete(track.uri); return next;
    });
    // Dual-write: clear not-jam, add jam
    v2Fetch("/api/v2/not-jams", "DELETE", { uri: track.uri });
    v2Fetch("/api/v2/jams", "POST", { uri: track.uri });
  }, [track, v2Fetch]);

  const removeFromJams = useCallback(() => {
    if (!track?.uri) return;
    setJams(prev => {
      const next = new Set(prev); next.delete(track.uri); return next;
    });
    v2Fetch("/api/v2/jams", "DELETE", { uri: track.uri });
  }, [track, v2Fetch]);

  const markNotJam = useCallback(() => {
    if (!track?.uri) return;
    setNotJams(prev => new Set([...prev, track.uri]));
    setJams(prev => {
      if (!prev.has(track.uri)) return prev;
      const next = new Set(prev); next.delete(track.uri); return next;
    });
    v2Fetch("/api/v2/jams", "DELETE", { uri: track.uri });
    v2Fetch("/api/v2/not-jams", "POST", { uri: track.uri });
  }, [track, v2Fetch]);

  const unmarkNotJam = useCallback(() => {
    if (!track?.uri) return;
    setNotJams(prev => {
      const next = new Set(prev); next.delete(track.uri); return next;
    });
    v2Fetch("/api/v2/not-jams", "DELETE", { uri: track.uri });
  }, [track, v2Fetch]);

  const captureDiscoveredTracks = useCallback((playlistTracks) => {
    if (!Array.isArray(playlistTracks)) return;
    const newOnes = playlistTracks.filter(t => t?.source === "discovered" && t?.uri);
    if (newOnes.length === 0) return;
    setDiscoveredTracks(prev => {
      const seen = new Set(prev.map(t => t.uri));
      const additions = newOnes.filter(t => !seen.has(t.uri));
      if (additions.length === 0) return prev;
      // Dual-write batch to Postgres
      v2Fetch("/api/v2/discovered-tracks", "POST", { tracks: additions });
      return [...additions, ...prev].slice(0, 200);
    });
  }, [v2Fetch]);

  // Derived state for the player tile buttons
  const currentIsJam = !!(track?.uri && jams.has(track.uri));
  const currentIsNotJam = !!(track?.uri && notJams.has(track.uri));

  // "Saved playlist" = most recent library entry (for the Resume tile),
  // unless the user explicitly dismissed it via the resume tile's Dismiss btn.
  const savedPlaylist = (() => {
    if (library.length === 0) return null;
    const sorted = [...library].sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    const latest = sorted[0];
    if (dismissedResumeId && latest.id === dismissedResumeId) return null;
    return latest;
  })();

  // Refs to avoid stale closures in async callbacks
  const playerRef = useRef(null);
  const deviceIdRef = useRef(null);
  const tokenRef = useRef(null);

  // Fetch fresh token (auto-refreshes via our backend).
  // When signed in via Supabase, use the v2 multi-user endpoint that reads
  // the per-user Spotify token from Postgres. Falls back to the legacy
  // single-user endpoint when not signed in (dev / pre-auth path).
  const fetchToken = useCallback(async () => {
    let res;
    try {
      const sess = (await window.supabase?.auth.getSession())?.data?.session;
      if (sess?.access_token) {
        res = await fetch("/api/v2/spotify-token", {
          headers: { Authorization: `Bearer ${sess.access_token}` },
        });
      } else {
        res = await fetch("/api/spotify-token");
      }
    } catch {
      res = await fetch("/api/spotify-token");
    }
    if (!res.ok) throw new Error(res.status === 401 ? "Spotify not connected" : "Token fetch failed");
    const data = await res.json();
    tokenRef.current = data.accessToken;
    return data.accessToken;
  }, []);

  // Initialize the player (idempotent; safe to call repeatedly)
  const initialize = useCallback(async () => {
    if (playerRef.current && deviceIdRef.current) return deviceIdRef.current;
    if (!window.Spotify?.Player) throw new Error("Spotify SDK not loaded");

    // If we have a stale playerRef but no deviceId (previous init failed or
    // was interrupted), clean it up before starting fresh so we don't leak
    // a dangling half-connected SDK instance.
    if (playerRef.current) {
      try { playerRef.current.disconnect(); } catch {}
      playerRef.current = null;
    }

    setStatus("connecting");
    setError(null);

    const token = await fetchToken();

    const p = new window.Spotify.Player({
      name: "Figjam Player",
      // Always fetch a fresh token. The SDK caches internally, but each time
      // it DOES ask, we want to hand it the freshest token our backend will
      // mint (the backend auto-refreshes if expired). On transient failure,
      // retry once before falling back to whatever we have — never hand the
      // SDK a token we know is stale.
      getOAuthToken: async (cb) => {
        try {
          cb(await fetchToken());
        } catch {
          try {
            await new Promise(r => setTimeout(r, 300));
            cb(await fetchToken());
          } catch {
            cb(tokenRef.current || token);
          }
        }
      },
      volume: 0.7,
    });

    // Set up ready listener BEFORE connect so we don't miss the event.
    // 30s timeout — SDK can be slow on first load, after a network hiccup,
    // or when the tab was backgrounded. On timeout, clean up the stale SDK
    // instance so the next attempt can create a fresh one.
    const readyPromise = new Promise((resolve, reject) => {
      const timeoutTimer = setTimeout(() => {
        try { p.removeListener("ready", onReady); } catch {}
        try { p.disconnect(); } catch {}
        if (playerRef.current === p) playerRef.current = null;
        reject(new Error("Player ready timed out after 30s — please try again"));
      }, 30000);

      const onReady = ({ device_id }) => {
        clearTimeout(timeoutTimer);
        try { p.removeListener("ready", onReady); } catch {}
        resolve(device_id);
      };
      p.addListener("ready", onReady);
    });

    // Ongoing listeners
    p.addListener("not_ready", () => setError("Player went offline"));
    p.addListener("initialization_error", ({ message }) => { setError(`Init: ${message}`); setStatus("error"); });
    // Spotify access tokens last ~1 hour. The SDK caches them internally and
    // doesn't proactively refresh, so on long sessions (e.g. a playlist that
    // took a while to generate followed by playback) we hit auth_error when
    // the cached token finally expires. Auto-recover: force a fresh token,
    // tear down the SDK, and let the next play call re-initialize cleanly.
    p.addListener("authentication_error", async ({ message }) => {
      console.warn("[player] authentication_error — attempting auto-recovery:", message);
      setStatus("connecting");
      setError("Refreshing Spotify connection…");
      try {
        await fetchToken(); // force fresh token from backend (auto-refreshes)
        try { p.disconnect(); } catch {}
        playerRef.current = null;
        deviceIdRef.current = null;
        // Re-init in the background; the next play action will use the new device.
        try {
          await initialize();
          setError(null);
        } catch (e) {
          setError(`Auth: ${e.message || "reconnect Spotify"}`);
          setStatus("error");
        }
      } catch (e) {
        setError(`Auth: ${e.message || message} — reconnect Spotify`);
        setStatus("error");
      }
    });
    p.addListener("account_error", ({ message }) => { setError(`${message} — Spotify Premium required`); setStatus("error"); });
    // Playback errors are often transient (network hiccups, token refresh
    // glitches, "no list loaded" during initial buffer). Music usually keeps
    // playing anyway. Show the error briefly and auto-clear after 4s so a
    // stale error doesn't hang around while audio is fine.
    p.addListener("playback_error", ({ message }) => {
      setError(`Playback: ${message}`);
      setTimeout(() => setError(prev => prev?.startsWith("Playback:") ? null : prev), 4000);
    });
    p.addListener("player_state_changed", (s) => {
      if (!s) return;
      setTrack({
        name: s.track_window.current_track?.name,
        artists: s.track_window.current_track?.artists?.map(a => a.name).join(", "),
        uri: s.track_window.current_track?.uri,
        albumArt: s.track_window.current_track?.album?.images?.[0]?.url,
        duration: s.duration,
        position: s.position,
      });
      // Persist current play position so a refresh / re-open can pick up
      // where we left off. Throttled to 3s — Spotify fires state-changed
      // on every metadata blip, so writing on each one would hammer
      // localStorage with no benefit.
      const uri = s.track_window?.current_track?.uri;
      const tsNow = Date.now();
      if (uri && tsNow - (resumeSavedAtRef.current || 0) > 3000) {
        resumeSavedAtRef.current = tsNow;
        try {
          localStorage.setItem(FIGJAM_RESUME_POINT_KEY, JSON.stringify({
            uri, positionMs: s.position, savedAt: new Date().toISOString(),
          }));
        } catch {}
      }
      setStatus(s.paused ? "paused" : "playing");
      // If audio is actually playing, clear any stale transient playback
      // error. Real fatal errors ("Auth:", "Init:", "account_error") are
      // kept so the user can act on them.
      if (!s.paused) {
        setError(prev => (prev?.startsWith("Playback:") || prev?.startsWith("Seek:") || prev?.startsWith("Volume:")) ? null : prev);
      }

      // Figjam always plays linearly AND auto-loops at the end. Enforce:
      //   - shuffle OFF (if user toggled it elsewhere)
      //   - repeat=context (if user turned repeat off or to repeat-one)
      // repeat_mode values: 0 = off, 1 = repeat-track, 2 = repeat-context
      //
      // Rate-limit guards: only fire each PUT at most once every 10s.
      // Spotify takes a moment to apply these, and we can get multiple
      // state_changed events during that window — without the guard, we'd
      // spam the API and burn rate-limit budget.
      const now = Date.now();
      if (s.shuffle && deviceIdRef.current && tokenRef.current
          && now - (shuffleEnforcedAtRef.current || 0) > 10000) {
        shuffleEnforcedAtRef.current = now;
        fetch(`https://api.spotify.com/v1/me/player/shuffle?state=false&device_id=${deviceIdRef.current}`, {
          method: "PUT", headers: { Authorization: `Bearer ${tokenRef.current}` },
        }).catch(() => {});
      }
      if (s.repeat_mode !== 2 && deviceIdRef.current && tokenRef.current
          && now - (repeatEnforcedAtRef.current || 0) > 10000) {
        repeatEnforcedAtRef.current = now;
        fetch(`https://api.spotify.com/v1/me/player/repeat?state=context&device_id=${deviceIdRef.current}`, {
          method: "PUT", headers: { Authorization: `Bearer ${tokenRef.current}` },
        }).catch(() => {});
      }
    });

    playerRef.current = p;
    const connected = await p.connect();
    if (!connected) throw new Error("Player connect() returned false");

    const devId = await readyPromise;
    deviceIdRef.current = devId;
    // Proactively force shuffle + repeat off on first connect so even pause/
    // resume via hardware keys or Spotify Connect hand-off starts linear.
    forceLinearPlayback(devId, token).catch(() => {});
    setStatus("ready");
    return devId;
  }, [fetchToken]);

  // Play a queue of tracks (initializes if needed)
  // `context` is optional activity metadata (elevation, duration, etc.) used
  // by the ElevationGraph tile to visualize ride progress alongside playback.
  const playQueue = useCallback(async (tracks, name = "", context = null, opts = {}) => {
    if (!tracks?.length) return;
    // opts.startIndex / opts.startPositionMs let resumeSaved pick up where
    // the user left off. Both default to 0 for fresh playback.
    const startIndex = Math.max(0, Math.min(Number(opts.startIndex) || 0, tracks.length - 1));
    const startPositionMs = Math.max(0, Number(opts.startPositionMs) || 0);
    try {
      setError(null);
      // Try to init; if it times out, retry ONCE. The SDK's ready event is
      // finicky on first attempt after a refresh — second attempt usually
      // works because the SDK has warmed up.
      let devId;
      try {
        devId = await initialize();
      } catch (initErr) {
        if (/timed out/.test(initErr.message)) {
          console.warn("[figjam-player] initial connect timed out — retrying once");
          devId = await initialize();
        } else {
          throw initErr;
        }
      }
      const token = await fetchToken();
      const uris = tracks.map(t => t.uri).filter(Boolean);

      // Force shuffle off + repeat off so the curated playlist plays in order.
      // The Web Playback SDK inherits account-level shuffle/repeat state, so
      // if the user had shuffle on elsewhere, songs would jump around. These
      // two calls are idempotent and cheap.
      await forceLinearPlayback(devId, token);

      setQueue(tracks);
      setQueueIndex(startIndex);
      setPlaylistName(name);

      // Add or update this playlist in the library (so it persists for later
      // replay + shows up in the library modal). Dedupe by name — replaying
      // an existing library entry just bumps its savedAt.
      upsertLibraryEntry(tracks, name, context);

      // Clear any previous dismissal — a new playlist should show resume next
      // time the player is closed.
      try { localStorage.removeItem(FIGJAM_DISMISSED_RESUME_KEY); } catch {}
      setDismissedResumeId(null);

      // Explicit offset.position = 0 forces start at the first URI even if
      // the user's account-level shuffle is still on (the shuffle=false call
      // above can be a no-op until there's an active context, so without an
      // explicit offset Spotify sometimes server-shuffles the URI array).
      const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${devId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ uris, offset: { position: startIndex }, position_ms: startPositionMs }),
      });
      if (!res.ok && res.status !== 204 && res.status !== 202) {
        const body = await res.text().catch(() => "");
        throw new Error(`Play failed: HTTP ${res.status} ${body.slice(0, 120)}`);
      }
      // Re-enforce shuffle off AFTER play starts — now there's an active
      // context, so this call actually sticks. Belt-and-suspenders alongside
      // the pre-play call and the player_state_changed re-enforcement.
      forceLinearPlayback(devId, token).catch(() => {});
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  }, [initialize, fetchToken]);

  const togglePlay = useCallback(async () => {
    if (playerRef.current) await playerRef.current.togglePlay();
  }, []);
  const nextTrack = useCallback(async () => {
    if (playerRef.current) await playerRef.current.nextTrack();
  }, []);
  const previousTrack = useCallback(async () => {
    if (playerRef.current) await playerRef.current.previousTrack();
  }, []);
  // Seek to a position in ms within the current track
  const seek = useCallback(async (positionMs) => {
    if (playerRef.current) {
      try { await playerRef.current.seek(positionMs); } catch (e) { setError(`Seek: ${e.message}`); }
    }
  }, []);
  // Jump to a specific index in the current queue. The Web Playback SDK has
  // no "jump" method, so we re-issue the play call with an offset.
  const jumpToIndex = useCallback(async (index) => {
    if (index < 0 || index >= queue.length) return;
    if (index === queueIndex) return;
    try {
      setError(null);
      // Pause the queueIndex sync effect for 4 seconds (see sync useEffect)
      manualJumpUntilRef.current = Date.now() + 4000;
      setQueueIndex(index);

      // We previously used the SDK's nextTrack/previousTrack for small jumps
      // to avoid Web API rate-limit cost, but rapid-fire SDK calls aren't
      // serialized by Spotify — calling nextTrack 3 times in a row often
      // resulted in only 1-2 actual track advances, so jumping landed one
      // (or more) tracks short of the clicked position. The Web API path
      // below is atomic (single PUT with explicit offset) and only adds
      // ~100-200ms latency per jump, which is unnoticeable in practice.
      // Reverted unused delta computation kept above for the manual-jump
      // pause window logic that already ran.
      const devId = deviceIdRef.current || (await initialize());
      const token = await fetchToken();
      const uris = queue.map(t => t.uri).filter(Boolean);
      const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${devId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ uris, offset: { position: index } }),
      });
      if (!res.ok && res.status !== 204 && res.status !== 202) {
        if (res.status === 429) {
          const wait = Math.min(10, parseInt(res.headers.get("Retry-After") || "2", 10));
          setError(`Rate limited — retrying in ${wait}s…`);
          await new Promise(r => setTimeout(r, wait * 1000));
          const retry = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${devId}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ uris, offset: { position: index } }),
          });
          if (retry.ok || retry.status === 204 || retry.status === 202) {
            setError(null);
            return;
          }
        }
        const body = await res.text().catch(() => "");
        throw new Error(`Jump failed: HTTP ${res.status} ${body.slice(0, 120)}`);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [queue, queueIndex, initialize, fetchToken]);
  // Set volume (0.0 – 1.0); updates local state optimistically
  const setVolume = useCallback(async (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (playerRef.current) {
      try { await playerRef.current.setVolume(clamped); } catch (e) { setError(`Volume: ${e.message}`); }
    }
  }, []);
  const stop = useCallback(async () => {
    if (playerRef.current) {
      try { await playerRef.current.pause(); } catch {}
    }
    setQueue([]);
    setTrack(null);
    setPlaylistName("");
    setStatus("idle");
    // Intentionally keep savedPlaylist in localStorage — closing the player
    // behaves just like a refresh: FigjamResumeTile will offer to replay it.
    // The user dismisses it explicitly via the Dismiss button (clearSaved).
  }, []);

  // Dismiss the "resume" offer — doesn't delete the entry, just hides the
  // tile. Library modal still shows it; any new playQueue clears dismissal.
  const clearSaved = useCallback(() => {
    if (!savedPlaylist) return;
    try { localStorage.setItem(FIGJAM_DISMISSED_RESUME_KEY, savedPlaylist.id); } catch {}
    setDismissedResumeId(savedPlaylist.id);
  }, [savedPlaylist]);

  // Replay whatever is in savedPlaylist (called from the Resume tile).
  const resumeSaved = useCallback(async () => {
    if (!savedPlaylist?.tracks?.length) return;
    // Read last-known play position. If the saved URI is in this playlist,
    // start playback at that index + offset within the track. Otherwise
    // fall back to track 0. Stale resume points (older than 7 days) are
    // ignored — assume the user wants a fresh start at that point.
    let resumeIndex = 0;
    let resumeMs = 0;
    try {
      const raw = localStorage.getItem(FIGJAM_RESUME_POINT_KEY);
      if (raw) {
        const pt = JSON.parse(raw);
        const ageMs = Date.now() - new Date(pt.savedAt || 0).getTime();
        if (pt.uri && ageMs < 7 * 24 * 60 * 60 * 1000) {
          const idx = savedPlaylist.tracks.findIndex(t => t.uri === pt.uri);
          if (idx >= 0) {
            resumeIndex = idx;
            resumeMs = Math.max(0, Number(pt.positionMs) || 0);
          }
        }
      }
    } catch {}
    await playQueue(
      savedPlaylist.tracks,
      savedPlaylist.name,
      savedPlaylist.activityContext || null,
      { startIndex: resumeIndex, startPositionMs: resumeMs }
    );
  }, [savedPlaylist, playQueue]);

  // ─── Library management ───────────────────────────────────────────────────
  // Upsert by a stable key of (name + first-track-uri). This means regenerating
  // the same activity with the same tracks bumps savedAt, but generating a
  // FRESH playlist for the same activity (even with the same name) creates a
  // new entry — since the track list will differ. Prevents accidental overwrite.
  const upsertLibraryEntry = useCallback((tracks, name, context) => {
    if (!tracks?.length) return;
    const firstUri = tracks[0]?.uri || "";
    setLibrary(prev => {
      const now = new Date().toISOString();
      const existingIdx = prev.findIndex(e =>
        e.name === name && (e.tracks?.[0]?.uri || "") === firstUri
      );
      let next;
      if (existingIdx >= 0) {
        next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx], tracks, savedAt: now,
          ...(context ? { activityContext: context } : {}),
        };
      } else {
        next = [{
          id: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          name, tracks, savedAt: now,
          favorited: false,
          ...(context ? { activityContext: context } : {}),
        }, ...prev];
      }
      const favs = next.filter(e => e.favorited);
      const recentsSorted = next.filter(e => !e.favorited)
        .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
      const recents = recentsSorted.slice(0, FIGJAM_LIBRARY_CAP);
      // Anything trimmed by the cap also needs to be deleted from Postgres,
      // otherwise the next sign-in / library sync pulls it back. Fire-and-
      // forget; only fires for UUID ids (server-saved entries).
      const trimmed = recentsSorted.slice(FIGJAM_LIBRARY_CAP);
      if (trimmed.length > 0) {
        (async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return;
            for (const entry of trimmed) {
              const looksLikeUuid = typeof entry.id === "string"
                && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entry.id);
              if (!looksLikeUuid) continue;
              try {
                await fetch(`/api/v2/playlists/${entry.id}`, {
                  method: "DELETE",
                  headers: { Authorization: `Bearer ${session.access_token}` },
                });
                console.log(`[figjam] cap-trim: removed "${entry.name}" from Postgres`);
              } catch (e) {
                console.warn(`[figjam] cap-trim delete failed: ${e.message}`);
              }
            }
          } catch (e) {
            console.warn("[figjam] cap-trim error:", e.message);
          }
        })();
      }
      return [...favs, ...recents];
    });

    // Phase B dual-write: ALSO save to Postgres if signed in via Supabase.
    // Fire-and-forget — failure doesn't break the localStorage path.
    // Reads still come from localStorage; this just primes Postgres for
    // when we cut over to read-from-Postgres in the next migration step.
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return; // not signed in via Supabase
        const res = await fetch("/api/v2/playlists", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            name,
            tracks,
            favorited: false,
            activityContext: context || null,
          }),
        });
        if (res.ok) {
          // Swap the local entry's id to the server-assigned UUID so
          // subsequent deletes / updates target the right row. Without this,
          // freshly-generated entries have ids like pl_TS_RAND and the
          // delete-from-Postgres path silently no-ops because the regex
          // rejects them as non-UUIDs.
          try {
            const { playlist: serverPlaylist } = await res.clone().json();
            const serverId = serverPlaylist?.id;
            if (serverId) {
              setLibrary(prev => prev.map(e =>
                (e.name === name && (e.tracks?.[0]?.uri || "") === firstUri)
                  ? { ...e, id: serverId } : e
              ));
            }
          } catch {}
          console.log(`[figjam] dual-write: saved "${name}" to Postgres`);
        } else {
          const err = await res.json().catch(() => ({}));
          console.warn(`[figjam] dual-write failed: ${err.error || res.status}`);
        }
      } catch (e) {
        console.warn("[figjam] dual-write error:", e.message);
      }
    })();
  }, []);

  // Public method to save a generated playlist WITHOUT playing it.
  // Called from the RidePlaylistBuilder on preview success so every AI
  // generation becomes a history entry even if the user closes the modal.
  const addToLibrary = useCallback((tracks, name, context) => {
    upsertLibraryEntry(tracks, name, context);
  }, [upsertLibraryEntry]);

  // Toggle favorite by id, or by currently-playing playlist name if no id given
  const toggleFavoriteCurrent = useCallback(() => {
    if (!playlistName) return;
    setLibrary(prev => prev.map(e => e.name === playlistName ? { ...e, favorited: !e.favorited } : e));
  }, [playlistName]);

  const toggleFavorite = useCallback((id) => {
    setLibrary(prev => prev.map(e => e.id === id ? { ...e, favorited: !e.favorited } : e));
  }, []);

  const deleteFromLibrary = useCallback((id) => {
    // Capture the entry we're about to delete BEFORE filtering, so we can
    // use its name for a fallback server lookup if the local id isn't a UUID
    // (e.g., entry was created this session and dual-write hasn't returned
    // a server id yet).
    let snapshot = null;
    setLibrary(prev => {
      snapshot = prev.find(e => e.id === id) || null;
      return prev.filter(e => e.id !== id);
    });
    setDismissedResumeId(prev => (prev === id ? null : prev));
    try {
      if (localStorage.getItem(FIGJAM_DISMISSED_RESUME_KEY) === id) {
        localStorage.removeItem(FIGJAM_DISMISSED_RESUME_KEY);
      }
    } catch {}
    // Server cleanup: delete the corresponding Postgres row so the next sync
    // doesn't bring this entry back. Prefer the UUID path; if the local id
    // isn't a UUID, fall back to GET-all + match-by-name + DELETE.
    const looksLikeUuid = typeof id === "string"
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const headers = { Authorization: `Bearer ${session.access_token}` };

        let serverId = looksLikeUuid ? id : null;
        if (!serverId && snapshot?.name) {
          // Fallback: look up the server-side row by name match. The POST
          // upsert keys on (user_id, name), so name is unique per user.
          const listRes = await fetch("/api/v2/playlists", { headers });
          if (listRes.ok) {
            const { playlists } = await listRes.json();
            const hit = (playlists || []).find(p => p.name === snapshot.name);
            if (hit) serverId = hit.id;
          }
        }

        if (!serverId) {
          console.log(`[figjam] delete: no server row to remove for "${snapshot?.name || id}"`);
          return;
        }

        const res = await fetch(`/api/v2/playlists/${serverId}`, {
          method: "DELETE", headers,
        });
        if (res.ok) {
          console.log(`[figjam] deleted playlist ${serverId} from Postgres`);
        } else {
          const err = await res.json().catch(() => ({}));
          console.warn(`[figjam] delete-from-server failed: ${err.error || res.status}`);
        }
      } catch (e) {
        console.warn("[figjam] delete-from-server error:", e.message);
      }
    })();
  }, []);

  const playFromLibrary = useCallback(async (id) => {
    const entry = library.find(e => e.id === id);
    if (!entry) return;
    await playQueue(entry.tracks, entry.name, entry.activityContext || null);
  }, [library, playQueue]);

  // Expose the current playlist's activity context (for ElevationGraph).
  // Found by matching the currently-playing name against the library.
  const currentLibraryEntry = playlistName
    ? library.find(e => e.name === playlistName) || null
    : null;
  const currentActivityContext = currentLibraryEntry?.activityContext || null;
  const currentIsFavorited = !!currentLibraryEntry?.favorited;

  // Sync queueIndex with the current playing track. Prefer the FORWARD
  // occurrence from the current index (Spotify normally advances linearly);
  // only fall back to the first overall occurrence if nothing forward matches.
  // This keeps the "now playing" highlight correct when the same song
  // legitimately appears more than once in the playlist.
  //
  // Skipped during a manual jump window — otherwise this effect would snap
  // queueIndex back to the OLD track's position while Spotify is still
  // switching to the new one, making clicks on queue rows feel broken.
  useEffect(() => {
    if (!track?.uri || queue.length === 0) return;
    if (Date.now() < manualJumpUntilRef.current) return; // manual jump in progress
    let idx = queue.findIndex((t, i) => i >= queueIndex && t.uri === track.uri);
    if (idx === -1) idx = queue.findIndex(t => t.uri === track.uri);
    if (idx !== -1 && idx !== queueIndex) setQueueIndex(idx);
  }, [track?.uri, queue, queueIndex]);

  // Keep the progress bar live. The SDK only fires player_state_changed on
  // state transitions (pause/resume/track change), so between transitions
  // `track.position` is stale. Poll player.getCurrentState() every 500ms
  // while playing to keep the scrubber accurate and to catch track-end/
  // track-start transitions faster than the SDK's native event cadence.
  useEffect(() => {
    if (status !== "playing" || !playerRef.current) return;
    const iv = setInterval(async () => {
      try {
        const s = await playerRef.current?.getCurrentState();
        if (!s) return;
        // Only update if the SDK actually reports a change — prevents
        // unnecessary re-renders when position hasn't moved yet.
        setTrack(prev => {
          if (!prev) {
            // SDK now has state but our track is null — seed it
            return {
              name: s.track_window?.current_track?.name,
              artists: s.track_window?.current_track?.artists?.map(a => a.name).join(", "),
              uri: s.track_window?.current_track?.uri,
              albumArt: s.track_window?.current_track?.album?.images?.[0]?.url,
              duration: s.duration,
              position: s.position,
            };
          }
          // Skip update if position / duration / uri haven't changed
          if (prev.position === s.position && prev.duration === s.duration && prev.uri === s.track_window?.current_track?.uri) {
            return prev;
          }
          return {
            ...prev,
            // Update track identity if it changed (SDK caught the track-end
            // transition before player_state_changed fired)
            name: s.track_window?.current_track?.name || prev.name,
            artists: s.track_window?.current_track?.artists?.map(a => a.name).join(", ") || prev.artists,
            uri: s.track_window?.current_track?.uri || prev.uri,
            albumArt: s.track_window?.current_track?.album?.images?.[0]?.url || prev.albumArt,
            duration: s.duration,
            position: s.position,
          };
        });
      } catch {}
    }, 500);
    return () => clearInterval(iv);
  }, [status]);


  // Warn on refresh / tab-close while the player is active. Browsers only
  // show their own generic "Leave site?" dialog (custom text is blocked) —
  // we just need to return a truthy string to trigger it. Handler is only
  // installed when there's actually something playing, so normal page loads
  // aren't interrupted.
  useEffect(() => {
    const hasActivity = queue.length > 0 || !!track;
    if (!hasActivity) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Refreshing will end your Figjam Player session. Are you sure?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [queue.length, track]);

  return {
    status, error, track, queue, queueIndex, playlistName, volume,
    savedPlaylist, resumeSaved, clearSaved, currentActivityContext,
    library, toggleFavorite, toggleFavoriteCurrent, currentIsFavorited,
    deleteFromLibrary, playFromLibrary, addToLibrary,
    // Jams / Not-Jam state + actions
    jams, notJams, discoveredTracks,
    currentIsJam, currentIsNotJam,
    addToJams, removeFromJams, markNotJam, unmarkNotJam, captureDiscoveredTracks,
    initialize, playQueue, togglePlay, nextTrack, previousTrack, seek, setVolume, jumpToIndex, stop,
  };
}

// ─── FigjamPlayerTile ───────────────────────────────────────────────────────
// Inline tile that replaces the Spotify "Now Playing" card when Figjam's web
// player is active. Same visual position, royal blue theme, with embedded
// transport controls + progress bar. When player stops, the tile disappears
// and the normal Now Playing card takes over again.
function FigjamPlayerTile({ player }) {
  const isMobile = useIsMobile();
  // Collapse state — persisted to localStorage so refreshes keep the choice
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("figjam:playerCollapsed") === "1"; } catch { return false; }
  });
  // All hooks MUST be called before any conditional return (React rule).
  // Keep them grouped at the top — subsequent early returns (`!player`,
  // `!hasActivity`) are safe because by then every hook has run.
  const [scrubMs, setScrubMs] = useState(null);
  const queueScrollRef = useRef(null);
  const queueRowRefs = useRef(new Map());

  // Follow the currently-playing song in the queue. Scroll so the PREVIOUS
  // song sits at the top edge and the current song is the second visible
  // row. IMPORTANT: deps are ONLY the exact primitives that should trigger
  // a re-scroll (queueIndex, collapsed, queue.length). If `player` were in
  // deps, this would fire 2x/second due to position polling and fight the
  // user's manual scroll.
  useEffect(() => {
    if (!player) return;
    if (collapsed) return;
    const { queue, queueIndex } = player;
    if (!queue || queue.length === 0) return;
    const container = queueScrollRef.current;
    if (!container) return;
    const targetIdx = Math.max(0, queueIndex - 1);
    const targetEl = queueRowRefs.current.get(targetIdx);
    if (!targetEl) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const delta = targetRect.top - containerRect.top;
    container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
  }, [player?.queueIndex, collapsed, player?.queue?.length]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("figjam:playerCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  if (!player) return null;
  const {
    status, error, track, queue, queueIndex, playlistName, volume,
    togglePlay, nextTrack, previousTrack, seek, setVolume, jumpToIndex, stop,
    toggleFavoriteCurrent, currentIsFavorited,
    currentIsJam, currentIsNotJam, addToJams, removeFromJams, markNotJam, unmarkNotJam,
  } = player;

  const hasActivity = queue.length > 0 || track;
  if (!hasActivity) return null;

  const currentQueueItem = queue[queueIndex];
  const displayName = track?.name || currentQueueItem?.name || "Loading…";
  const displayArtists = track?.artists || currentQueueItem?.artists || "";
  const segmentLabel = currentQueueItem?.segmentLabel;
  const albumArt = track?.albumArt || currentQueueItem?.albumArt;
  const duration = track?.duration || 0;
  const livePos = track?.position || 0;
  // Show the drag position while scrubbing, otherwise the live position
  const displayPos = scrubMs != null ? scrubMs : livePos;
  const progressPct = duration ? Math.min(100, (displayPos / duration) * 100) : 0;
  const isPaused = status !== "playing";
  const fmtMs = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const color = "#1DB954"; // Spotify green — matches playlist history theme
  const bg = "linear-gradient(135deg,#041c0b 0%,#0d3a1e 55%,#061a0a 100%)";

  // (Queue follow-scroll useEffect moved to top of component so it runs
  //  unconditionally — required by React's rules of hooks.)

  return (
    <div style={{ marginBottom: 18, marginTop: 4, position: "relative" }}>
      {/* Collapse chevron — pinned to top-right of the whole player tile,
          outside the card body. Always accessible regardless of header wrap. */}
      <button
        onClick={toggleCollapsed}
        title={collapsed ? "Expand player" : "Collapse player"}
        style={{
          position: "absolute", top: 0, right: 0, zIndex: 3,
          background: `${color}22`,
          border: `1px solid ${color}`,
          borderRadius: 6, padding: "3px 10px",
          color,
          fontSize: 13, fontWeight: 700, cursor: "pointer",
          fontFamily: "monospace", lineHeight: 1,
          boxShadow: `0 0 6px ${color}55`,
        }}
      >
        {collapsed ? "⌄" : "⌃"}
      </button>

      {/* Header bar — distinct, no timeline connector */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "0 40px 14px 4px", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{
            display: "inline-block", width: 22, height: 22, borderRadius: "50%",
            background: status === "playing" ? "#1DB954" : color,
            boxShadow: `0 0 24px ${status === "playing" ? "#1DB954" : color}, 0 0 8px ${status === "playing" ? "#1DB954" : color}`,
            animation: status === "playing" ? "figjamPulse 1.4s ease-in-out infinite" : "none",
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 33, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase",
            color: "#fff", fontFamily: "monospace", lineHeight: 1,
          }}>
            FigJam
          </span>
          {status === "playing" ? (
            <span style={{
              fontSize: 18, fontWeight: 900, letterSpacing: "0.2em",
              color: "#FC4C02", fontFamily: "monospace",
              textShadow: "0 0 12px rgba(252,76,2,0.7)",
              animation: "figjamLivePulse 1.2s ease-in-out infinite",
              padding: "2px 10px", border: "2px solid #FC4C02", borderRadius: 6,
              whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ display: "inline-block" }}>●</span>
              <span>LIVE</span>
            </span>
          ) : (
            <span style={{
              fontSize: 14, fontWeight: 700, letterSpacing: "0.18em",
              color: "rgba(255,255,255,0.5)", fontFamily: "monospace",
            }}>
              {isPaused ? "PAUSED" : "READY"}
            </span>
          )}
        </div>
      </div>

      {/* Collapsed "now playing" mini-bar — only shown when tile is collapsed
          so user can still see what's playing without expanding */}
      {collapsed && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", marginBottom: 10,
          background: bg,
          border: `1px solid ${color}66`,
          borderRadius: 10,
        }}>
          {albumArt ? (
            <img src={albumArt} alt="" style={{ width: 36, height: 36, borderRadius: 4, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: 4, background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🎵</div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display',serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayName}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayArtists}
            </div>
          </div>
          {/* Compact Jams buttons for collapsed state */}
          <button
            onClick={() => currentIsJam ? removeFromJams() : addToJams()}
            title={currentIsJam ? "Remove from my Jams" : "Add to my Jams"}
            disabled={!track?.uri}
            style={{
              background: currentIsJam ? "rgba(29,185,84,0.25)" : "rgba(255,255,255,0.06)",
              border: `1px solid ${currentIsJam ? "#1DB954" : "rgba(255,255,255,0.12)"}`,
              color: currentIsJam ? "#1DB954" : "rgba(255,255,255,0.7)",
              borderRadius: 6, width: 32, height: 32, padding: 0,
              fontSize: 14, cursor: track?.uri ? "pointer" : "not-allowed", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            👍
          </button>
          <button
            onClick={() => currentIsNotJam ? unmarkNotJam() : markNotJam()}
            title={currentIsNotJam ? "Undo" : "Not my jam"}
            disabled={!track?.uri}
            style={{
              background: currentIsNotJam ? "rgba(252,76,2,0.2)" : "rgba(255,255,255,0.06)",
              border: `1px solid ${currentIsNotJam ? "#FC4C02" : "rgba(255,255,255,0.12)"}`,
              color: currentIsNotJam ? "#FC4C02" : "rgba(255,255,255,0.7)",
              borderRadius: 6, width: 32, height: 32, padding: 0,
              fontSize: 14, cursor: track?.uri ? "pointer" : "not-allowed", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            👎
          </button>
          <button
            onClick={togglePlay}
            style={{
              background: color, border: "none", borderRadius: 18, width: 36, height: 36,
              color: "#fff", fontSize: 14, cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 2px 8px ${color}88`,
            }}
            title={isPaused ? "Play" : "Pause"}
          >
            {isPaused ? "▶" : "⏸"}
          </button>
        </div>
      )}

      {/* Full-width tile — no left timeline indent, strong border + glow */}
      {!collapsed && (
      <div style={{
        background: bg,
        border: `2px solid ${color}`,
        borderRadius: 16,
        padding: "16px 18px",
        boxShadow: `0 0 0 1px ${color}33, 0 8px 32px rgba(65,105,225,0.35), inset 0 1px 0 rgba(255,255,255,0.08)`,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Subtle radial highlight in background */}
        <div style={{
          position: "absolute", top: -60, right: -40, width: 220, height: 220,
          background: `radial-gradient(circle, ${color}22 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />

        {/* Top-right: favorite heart (collapse chevron is in the outer
            header above, next to the track counter) */}
        <button
          onClick={toggleFavoriteCurrent}
          title={currentIsFavorited ? "Remove from favorites" : "Save to favorites"}
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 2,
            width: 28, height: 28, borderRadius: 14,
            background: currentIsFavorited ? "rgba(252,76,2,0.2)" : "rgba(255,255,255,0.08)",
            border: `1px solid ${currentIsFavorited ? "rgba(252,76,2,0.6)" : "rgba(255,255,255,0.15)"}`,
            color: currentIsFavorited ? "#FC4C02" : "rgba(255,255,255,0.7)",
            fontSize: 13, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {currentIsFavorited ? "❤️" : "🤍"}
        </button>

        {/* Two-column layout: left = current track/controls, right = queue */}
        <div className="figjam-tile-cols" style={{
          display: "flex", gap: 18, position: "relative",
        }}>
        {/* ── LEFT COLUMN: current playlist, track, progress, controls, volume ── */}
        <div style={{ flex: 1.5, minWidth: 0 }}>
        {/* Playlist name — the big prominent label */}
        {playlistName && (
          <div style={{
            fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 4,
            fontFamily: "'Playfair Display',serif", lineHeight: 1.25, position: "relative",
          }}>
            {playlistName}
          </div>
        )}
        {/* Build summary pills — capture how this playlist was generated so
            the user can replicate it. Pulled from player.currentActivityContext
            which is set when the entry was saved with buildSettings. Older
            entries (pre-buildSettings) just won't show the pill row. */}
        {playlistName && player.currentActivityContext?.buildSettings && (() => {
          const bs = player.currentActivityContext.buildSettings;
          const pills = [];
          if (bs.presetLabel) {
            pills.push({ key: "preset", label: bs.presetLabel, color: "#E8D44D" });
          } else if (bs.notes) {
            const short = bs.notes.length > 32 ? bs.notes.slice(0, 30) + "…" : bs.notes;
            pills.push({ key: "notes", label: `📝 ${short}`, color: "#9CA3AF" });
          }
          if (bs.varietyMode) {
            const vlabel = { discover: "✨ Wide variety", balanced: "⚖️ Balanced", favorites: "💛 Favorites" }[bs.varietyMode] || bs.varietyMode;
            pills.push({ key: "variety", label: vlabel, color: "#60A5FA" });
          }
          if (bs.discoveryMode === "explore") {
            pills.push({ key: "discovery", label: "🔭 Explore mode", color: "#A78BFA" });
          }
          if (pills.length === 0) return null;
          return (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8, position: "relative" }}>
              {pills.map(p => (
                <span key={p.key}
                  title={p.key === "notes" ? bs.notes : p.label}
                  style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                    fontFamily: "'DM Sans',sans-serif",
                    color: p.color,
                    background: `${p.color}1A`,
                    border: `1px solid ${p.color}55`,
                    padding: "3px 9px", borderRadius: 12,
                    whiteSpace: "nowrap", maxWidth: "100%",
                    overflow: "hidden", textOverflow: "ellipsis",
                  }}
                >{p.label}</span>
              ))}
            </div>
          );
        })()}
        {playlistName && queue.length > 0 && (() => {
          // Total: sum of all queue track durations. Remaining: tracks after
          // the current one, plus the time left in the current track. Falls
          // back to a per-track avg when a track has no durationMs (rare).
          const avgFallbackMs = 3.5 * 60 * 1000;
          const dur = (t) => t.durationMs || t.duration_ms || avgFallbackMs;
          const totalMs = queue.reduce((a, t) => a + dur(t), 0);
          const consumedBefore = queue.slice(0, queueIndex).reduce((a, t) => a + dur(t), 0);
          const consumedCurrent = Math.min(displayPos || 0, duration || 0);
          const remainingMs = Math.max(0, totalMs - consumedBefore - consumedCurrent);
          const toMin = (ms) => Math.max(0, Math.round(ms / 60000));
          return (
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
              color: "#E8D44D", fontFamily: "monospace", marginBottom: 14, position: "relative",
            }}>
              {toMin(totalMs)} min total · {toMin(remainingMs)} min left
            </div>
          );
        })()}

        {/* Segment chip (if AI-matched to terrain) */}
        {segmentLabel && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(29,185,84,0.15)", border: "1px solid rgba(29,185,84,0.4)",
            padding: "4px 10px", borderRadius: 12, marginBottom: 12, position: "relative",
          }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Now on
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#1DB954", fontFamily: "'Playfair Display',serif" }}>
              {segmentLabel}
            </span>
          </div>
        )}

        {/* Current track row — on mobile, stack centered (album art big +
            centered, title/artist below). On desktop, side-by-side as
            before. The mobile layout makes the album art the visual hero
            and pairs naturally with the enlarged control row below. */}
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: isMobile ? 12 : 14,
          position: "relative",
          textAlign: isMobile ? "center" : "left",
        }}>
          {albumArt ? (
            <img src={albumArt} alt="" style={{
              width: isMobile ? 220 : 72, height: isMobile ? 220 : 72,
              borderRadius: isMobile ? 14 : 8, flexShrink: 0,
              boxShadow: `0 4px 16px rgba(0,0,0,0.5), 0 0 0 1px ${color}44`,
            }} />
          ) : (
            <div style={{
              width: isMobile ? 220 : 72, height: isMobile ? 220 : 72,
              borderRadius: isMobile ? 14 : 8, flexShrink: 0,
              background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: isMobile ? 80 : 28,
            }}>🎵</div>
          )}
          <div style={{ minWidth: 0, flex: isMobile ? "0 0 auto" : 1, width: isMobile ? "100%" : "auto" }}>
            <div style={{
              fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em",
              color: "rgba(255,255,255,0.45)", fontFamily: "monospace", marginBottom: 2,
            }}>
              NOW PLAYING
            </div>
            <div style={{
              fontSize: isMobile ? 19 : 17, fontWeight: 700, color: "#fff",
              fontFamily: "'Playfair Display',serif",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {displayName}
            </div>
            <div style={{
              fontSize: isMobile ? 14 : 13, color: "rgba(255,255,255,0.7)", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {displayArtists}
            </div>
          </div>
        </div>

        {/* Seekable progress bar — drag or click to jump */}
        <div style={{ marginTop: 16, marginBottom: 14, position: "relative" }}>
          <input
            type="range"
            min={0}
            max={Math.max(duration, 1)}
            step={1000}
            value={displayPos}
            onChange={(e) => setScrubMs(Number(e.target.value))}
            onMouseUp={(e) => { seek(Number(e.target.value)); setScrubMs(null); }}
            onTouchEnd={(e) => { const v = Number(e.target.value); seek(v); setScrubMs(null); }}
            onKeyUp={(e) => { seek(Number(e.target.value)); setScrubMs(null); }}
            className="figjam-scrubber"
            disabled={!duration}
            style={{
              width: "100%", appearance: "none", WebkitAppearance: "none",
              height: 6, borderRadius: 3, outline: "none", cursor: duration ? "pointer" : "default",
              background: `linear-gradient(to right, ${color} 0%, #5DE08C ${progressPct}%, rgba(255,255,255,0.1) ${progressPct}%, rgba(255,255,255,0.1) 100%)`,
            }}
          />
          <div style={{
            display: "flex", justifyContent: "space-between",
            fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: "monospace",
            marginTop: 4,
          }}>
            <span>{fmtMs(displayPos)}</span>
            <span>{fmtMs(duration)}</span>
          </div>
        </div>

        {/* Controls — sizes bumped on mobile so they're easy to hit with
            a thumb during a ride. Desktop sizes unchanged. */}
        <div style={{ display: "flex", gap: isMobile ? 18 : 8, alignItems: "center", justifyContent: "center", position: "relative" }}>
          <button
            onClick={previousTrack}
            style={{
              ...figjamBtnStyle,
              width: isMobile ? 64 : 40, height: isMobile ? 64 : 40,
              fontSize: isMobile ? 24 : 15,
              borderRadius: isMobile ? 32 : 20,
            }}
            title="Previous"
          >⏮</button>
          <button
            onClick={togglePlay}
            style={{
              ...figjamBtnStyle, background: color, color: "#fff",
              width: isMobile ? 84 : 48, height: isMobile ? 84 : 48,
              fontSize: isMobile ? 32 : 18,
              borderRadius: isMobile ? 42 : 24,
              boxShadow: `0 4px 14px ${color}88`,
            }}
            title={isPaused ? "Play" : "Pause"}
          >
            {isPaused ? "▶" : "⏸"}
          </button>
          <button
            onClick={nextTrack}
            style={{
              ...figjamBtnStyle,
              width: isMobile ? 64 : 40, height: isMobile ? 64 : 40,
              fontSize: isMobile ? 24 : 15,
              borderRadius: isMobile ? 32 : 20,
            }}
            title="Next"
          >⏭</button>
        </div>

        {/* Jams / Not-my-jam feedback buttons for the currently-playing track */}
        <div style={{
          display: "flex", gap: 8, alignItems: "stretch", justifyContent: "center",
          marginTop: 12, position: "relative",
        }}>
          <button
            onClick={() => currentIsJam ? removeFromJams() : addToJams()}
            title={currentIsJam ? "Remove from your Jams" : "Add to my Jams — remembered by Figjam for future playlists"}
            disabled={!track?.uri}
            style={{
              flex: 1, padding: "8px 10px",
              background: currentIsJam ? "rgba(29,185,84,0.25)" : "rgba(255,255,255,0.06)",
              border: `1px solid ${currentIsJam ? "#1DB954" : "rgba(255,255,255,0.12)"}`,
              color: currentIsJam ? "#1DB954" : "rgba(255,255,255,0.75)",
              borderRadius: 8,
              fontSize: 12, fontWeight: 700,
              cursor: track?.uri ? "pointer" : "not-allowed",
              fontFamily: "'DM Sans',sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              boxShadow: currentIsJam ? "0 0 8px rgba(29,185,84,0.35)" : "none",
            }}
          >
            <span style={{ fontSize: 14 }}>👍</span>
            {currentIsJam ? "In my Jams" : "Add to my Jams"}
          </button>
          <button
            onClick={() => currentIsNotJam ? unmarkNotJam() : markNotJam()}
            title={currentIsNotJam ? "Undo — bring this song back" : "Not my jam — don't recommend this again"}
            disabled={!track?.uri}
            style={{
              flex: 1, padding: "8px 10px",
              background: currentIsNotJam ? "rgba(252,76,2,0.2)" : "rgba(255,255,255,0.06)",
              border: `1px solid ${currentIsNotJam ? "#FC4C02" : "rgba(255,255,255,0.12)"}`,
              color: currentIsNotJam ? "#FC4C02" : "rgba(255,255,255,0.75)",
              borderRadius: 8,
              fontSize: 12, fontWeight: 700,
              cursor: track?.uri ? "pointer" : "not-allowed",
              fontFamily: "'DM Sans',sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              boxShadow: currentIsNotJam ? "0 0 8px rgba(252,76,2,0.35)" : "none",
            }}
          >
            <span style={{ fontSize: 14 }}>👎</span>
            {currentIsNotJam ? "Marked" : "Not my jam"}
          </button>
        </div>

        {/* Volume slider */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginTop: 14, padding: "0 2px", position: "relative",
        }}>
          <button
            onClick={() => setVolume(volume > 0 ? 0 : 0.7)}
            style={{
              background: "transparent", border: "none", padding: 0,
              color: "rgba(255,255,255,0.7)", fontSize: 16, cursor: "pointer",
              width: 26, textAlign: "center",
            }}
            title={volume > 0 ? "Mute" : "Unmute"}
          >
            {volume === 0 ? "🔇" : volume < 0.4 ? "🔈" : volume < 0.75 ? "🔉" : "🔊"}
          </button>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            onChange={(e) => setVolume(Number(e.target.value) / 100)}
            className="figjam-volume"
            style={{
              flex: 1, appearance: "none", WebkitAppearance: "none",
              height: 4, borderRadius: 2, outline: "none", cursor: "pointer",
              background: `linear-gradient(to right, ${color} 0%, #5DE08C ${Math.round(volume * 100)}%, rgba(255,255,255,0.1) ${Math.round(volume * 100)}%, rgba(255,255,255,0.1) 100%)`,
            }}
          />
          <span style={{
            fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: "monospace",
            width: 30, textAlign: "right",
          }}>
            {Math.round(volume * 100)}
          </span>
        </div>
        </div>
        {/* ── RIGHT COLUMN: song queue with current highlighted ── */}
        {queue.length > 0 && (
          <div style={{
            flex: 1, minWidth: 0,
            background: "rgba(0,0,0,0.2)",
            border: `1px solid ${color}22`,
            borderRadius: 10, padding: "10px 4px 10px 10px",
            display: "flex", flexDirection: "column",
            maxHeight: 380,
          }}>
            <div style={{
              fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em",
              color: "rgba(255,255,255,0.55)", fontFamily: "monospace",
              padding: "2px 6px 8px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span>Queue</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>{queueIndex + 1} / {queue.length}</span>
            </div>
            <div
              ref={queueScrollRef}
              className="figjam-queue-scroll"
              style={{
                overflowY: "auto", flex: 1, paddingRight: 10, marginRight: -6,
              }}
            >
              {queue.map((t, i) => {
                const isCurrent = i === queueIndex;
                const isPast = i < queueIndex;
                return (
                  <div
                    key={`${t.uri || "q"}-${i}`}
                    ref={(el) => {
                      if (el) queueRowRefs.current.set(i, el);
                      else queueRowRefs.current.delete(i);
                    }}
                    onClick={() => !isCurrent && jumpToIndex(i)}
                    style={{
                      display: "flex", alignItems: "center", gap: isMobile ? 11 : 8,
                      padding: isMobile ? "9px 8px" : "6px 6px",
                      marginBottom: isMobile ? 4 : 2,
                      borderRadius: isMobile ? 8 : 6,
                      cursor: isCurrent ? "default" : "pointer",
                      background: isCurrent ? `${color}33` : "transparent",
                      border: `1px solid ${isCurrent ? color : "transparent"}`,
                      opacity: isPast ? 0.38 : 1,
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                    onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}
                    title={isCurrent ? "Now playing" : `Jump to: ${t.name}`}
                  >
                    <div style={{
                      width: isMobile ? 22 : 18, flexShrink: 0, textAlign: "center",
                      fontSize: isMobile ? 12 : 10, fontFamily: "monospace",
                      color: isCurrent ? color : "rgba(255,255,255,0.35)",
                      fontWeight: isCurrent ? 700 : 400,
                    }}>
                      {isCurrent ? "▶" : i + 1}
                    </div>
                    {t.albumArt ? (
                      <img
                        src={t.albumArt}
                        alt=""
                        style={{
                          width: isMobile ? 48 : 28, height: isMobile ? 48 : 28,
                          borderRadius: isMobile ? 5 : 3, flexShrink: 0,
                          boxShadow: isCurrent ? `0 0 0 1px ${color}` : "none",
                        }}
                      />
                    ) : (
                      <div style={{
                        width: isMobile ? 48 : 28, height: isMobile ? 48 : 28,
                        borderRadius: isMobile ? 5 : 3, flexShrink: 0,
                        background: "rgba(255,255,255,0.05)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: isMobile ? 18 : 12,
                      }}>🎵</div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: isMobile ? 14 : 11, fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent ? "#fff" : "rgba(255,255,255,0.85)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontFamily: "'DM Sans',sans-serif",
                      }}>
                        {t.name || "Unknown"}
                      </div>
                      <div style={{
                        fontSize: isMobile ? 12 : 9,
                        color: isCurrent ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.45)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontFamily: "monospace",
                        marginTop: isMobile ? 3 : 1,
                      }}>
                        {t.artists || ""}
                        {t.segmentLabel && (
                          <span style={{ color: "#1DB954", marginLeft: 4 }}>· {t.segmentLabel}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>

        {error && (
          <div style={{
            fontSize: 11, color: "#FC4C02", marginTop: 10,
            background: "rgba(252,76,2,0.1)", padding: "6px 10px", borderRadius: 6, position: "relative",
          }}>
            {error}
          </div>
        )}
      </div>
      )}

      {/* Keyframes + slider thumb styling */}
      <style>{`
        @keyframes figjamPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes figjamLivePulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 12px rgba(252,76,2,0.7); }
          50% { opacity: 0.6; box-shadow: 0 0 24px rgba(252,76,2,1); }
        }
        .figjam-scrubber::-webkit-slider-thumb,
        .figjam-volume::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          background: #fff; border: 2px solid #1DB954;
          border-radius: 50%; cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4), 0 0 10px rgba(29,185,84,0.5);
        }
        .figjam-scrubber::-webkit-slider-thumb { width: 16px; height: 16px; margin-top: -5px; }
        .figjam-volume::-webkit-slider-thumb { width: 12px; height: 12px; margin-top: -4px; }
        .figjam-scrubber::-moz-range-thumb,
        .figjam-volume::-moz-range-thumb {
          background: #fff; border: 2px solid #1DB954;
          border-radius: 50%; cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4), 0 0 10px rgba(29,185,84,0.5);
        }
        .figjam-scrubber::-moz-range-thumb { width: 16px; height: 16px; }
        .figjam-volume::-moz-range-thumb { width: 12px; height: 12px; }
        .figjam-scrubber:disabled { opacity: 0.4; cursor: default; }

        /* Queue scrollbar — wider, brighter, clickable */
        .figjam-queue-scroll::-webkit-scrollbar {
          width: 12px;
        }
        .figjam-queue-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 6px;
          margin: 2px 0;
        }
        .figjam-queue-scroll::-webkit-scrollbar-thumb {
          background: rgba(29,185,84,0.55);
          border-radius: 6px;
          border: 2px solid transparent;
          background-clip: padding-box;
          cursor: pointer;
          box-shadow: 0 0 4px rgba(29,185,84,0.35);
          transition: background 0.15s ease;
        }
        .figjam-queue-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(29,185,84,0.9);
          background-clip: padding-box;
          cursor: pointer;
          box-shadow: 0 0 8px rgba(29,185,84,0.6);
        }
        .figjam-queue-scroll::-webkit-scrollbar-thumb:active {
          background: rgba(29,185,84,1);
          background-clip: padding-box;
          cursor: grabbing;
        }
        /* Firefox fallback */
        .figjam-queue-scroll {
          scrollbar-width: auto;
          scrollbar-color: rgba(29,185,84,0.7) rgba(255,255,255,0.05);
        }

        @media (max-width: 560px) {
          .figjam-tile-cols { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
}

const figjamBtnStyle = {
  background: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 20,
  width: 40, height: 40,
  color: "#fff",
  fontSize: 15,
  cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
};

// ─── FigjamResumeTile ───────────────────────────────────────────────────────
// Shown when the player is idle but a previous AI playlist is stashed in
// localStorage (i.e. the user just refreshed). One click resumes playback
// through the SDK so they don't have to re-run the AI generation flow.
function FigjamResumeTile({ player }) {
  if (!player?.savedPlaylist) return null;
  // Don't show if the player is already active (FigjamPlayerTile takes over)
  const hasActivity = player.queue.length > 0 || player.track;
  if (hasActivity) return null;

  const { name, tracks, savedAt } = player.savedPlaylist;
  const trackCount = tracks?.length || 0;
  const savedAgo = savedAt ? ago(savedAt) : "";
  const savedExact = savedAt ? dateTime(savedAt) : "";
  const connecting = player.status === "connecting";

  const color = "#1DB954"; // match the player tile's green theme

  return (
    <div style={{ marginBottom: 18, marginTop: 4 }}>
      {/* Header bar — mirrors the active FigjamPlayerTile header lockup
          (pulsing dot + big FIGJAM wordmark + pill) so the transition from
          "saved, ready to resume" to "actively playing" is visually
          seamless. The dot pulses to keep brand presence on hard refresh. */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 14,
        padding: "0 4px 14px", flexWrap: "wrap",
      }}>
        <span style={{
          display: "inline-block", width: 22, height: 22, borderRadius: "50%",
          background: color,
          boxShadow: `0 0 24px ${color}, 0 0 8px ${color}`,
          animation: "figjamPulse 1.4s ease-in-out infinite",
          flexShrink: 0,
        }} />
        <span style={{
          fontSize: 33, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase",
          color: "#fff", fontFamily: "monospace", lineHeight: 1,
        }}>
          FigJam
        </span>
        <span style={{
          fontSize: 14, fontWeight: 700, letterSpacing: "0.18em",
          color: "rgba(255,255,255,0.5)", fontFamily: "monospace",
        }}>
          READY
        </span>
      </div>

      <div style={{
        background: "linear-gradient(135deg,#061a0a 0%,#0d3a1e 55%,#061a0a 100%)",
        border: `1px dashed ${color}77`,
        borderRadius: 16,
        padding: "16px 18px",
        boxShadow: `0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase",
          color: "#E8D44D", fontFamily: "monospace", marginBottom: 4,
        }}>
          🤖 AI Playlist · Ready to Resume
        </div>
        <div style={{
          fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 6,
          fontFamily: "'Playfair Display',serif", lineHeight: 1.3,
        }}>
          {name || "Untitled playlist"}
        </div>
        <div style={{
          fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: "monospace", marginBottom: 14,
        }} title={savedExact}>
          {trackCount} tracks · generated {savedAgo}
          {savedExact && <span style={{ color: "rgba(255,255,255,0.35)" }}> · {savedExact}</span>}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={player.resumeSaved}
            disabled={connecting}
            style={{
              flex: 1, padding: "12px", background: connecting ? "rgba(65,105,225,0.4)" : color,
              border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14,
              cursor: connecting ? "wait" : "pointer", fontFamily: "'DM Sans',sans-serif",
              boxShadow: `0 4px 14px ${color}88`,
            }}
          >
            {connecting ? "⏳ Connecting…" : "▶ Resume Playlist"}
          </button>
          <button
            onClick={player.clearSaved}
            style={{
              padding: "12px 16px", background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
              color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: 13,
              cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
            }}
            title="Dismiss — don't show this resume option again"
          >
            Dismiss
          </button>
        </div>

        {player.error && (
          <div style={{
            fontSize: 11, color: "#FC4C02", marginTop: 10,
            background: "rgba(252,76,2,0.1)", padding: "6px 10px", borderRadius: 6,
          }}>
            {player.error}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ElevationGraph ─────────────────────────────────────────────────────────
// Strava-orange tile below the player. Shows the ride's elevation profile
// as an SVG curve with a live position marker that moves as the playlist
// plays. Progress is measured by playlist elapsed / playlist total, mapped
// proportionally onto the ride's total duration (the two clocks are
// independent, but the visual intuition "I'm ⅓ through the playlist = ⅓
// through the ride" is what gives this feature its value).
function ElevationGraph({ player }) {
  // Tap a song marker to open a tooltip with info + jump button. Click the
  // same marker again (or the × in the tooltip) to dismiss. Works on both
  // desktop and touch devices.
  const [selectedSong, setSelectedSong] = useState(null);
  // Collapse state — persisted so refreshes preserve the choice
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("figjam:graphCollapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("figjam:graphCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  if (!player) return null;
  const { track, queue, queueIndex, currentActivityContext: ctx, jumpToIndex, playlistName } = player;
  // Use the user-entered custom name from the playlist (first chunk before
  // " · "). Falls back to the full playlist name, then the Strava activity
  // name, then a generic label.
  const displayTitle =
    (playlistName && playlistName.split(" · ")[0]) ||
    playlistName ||
    ctx?.activityName ||
    "Ride";

  // Only render when the player is active AND we have elevation data for
  // this playlist (playlists generated before this feature shipped won't).
  const hasActivity = queue.length > 0 || !!track;
  if (!hasActivity) return null;
  if (!ctx?.elevation?.length) return null;

  // ── Progress calculation ─────────────────────────────────────────────────
  // Elapsed = sum of completed track durations + current track position.
  // Total  = sum of all track durations.
  const elapsedMs = queue
    .slice(0, queueIndex)
    .reduce((sum, t) => sum + (t.durationMs || 0), 0)
    + (track?.position || 0);
  const totalPlaylistMs = queue.reduce((sum, t) => sum + (t.durationMs || 0), 0);
  const fraction = totalPlaylistMs > 0 ? Math.min(1, elapsedMs / totalPlaylistMs) : 0;

  // ── SVG geometry ─────────────────────────────────────────────────────────
  const W = 560;  // viewBox width — scales responsively
  const H = 140;  // viewBox height
  const PAD_L = 34, PAD_R = 10, PAD_T = 18, PAD_B = 22;

  const pts = ctx.elevation;
  const alts = pts.map(p => p.alt);
  const times = pts.map(p => p.t);
  const minAlt = Math.min(...alts);
  const maxAlt = Math.max(...alts);
  const altRange = Math.max(1, maxAlt - minAlt); // avoid div-by-zero on flat rides
  const minT = times[0];
  const maxT = times[times.length - 1];
  const tRange = Math.max(1, maxT - minT);

  const xFor = (t) => PAD_L + ((t - minT) / tRange) * (W - PAD_L - PAD_R);
  const yFor = (alt) => PAD_T + (1 - (alt - minAlt) / altRange) * (H - PAD_T - PAD_B);

  // Build the elevation path
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(p.t).toFixed(1)} ${yFor(p.alt).toFixed(1)}`).join(" ");
  // And the fill polygon (path + bottom corners)
  const fillPath = linePath
    + ` L ${xFor(maxT).toFixed(1)} ${H - PAD_B}`
    + ` L ${xFor(minT).toFixed(1)} ${H - PAD_B}`
    + " Z";

  // Current position marker x
  const markerX = PAD_L + fraction * (W - PAD_L - PAD_R);

  // Find the altitude at the current position (interpolated)
  const currentT = minT + fraction * tRange;
  let currentAlt = alts[0];
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].t >= currentT) {
      const prev = pts[i - 1];
      const next = pts[i];
      const f = (currentT - prev.t) / Math.max(1, next.t - prev.t);
      currentAlt = prev.alt + f * (next.alt - prev.alt);
      break;
    }
  }
  const markerY = yFor(currentAlt);

  // ── Format helpers ───────────────────────────────────────────────────────
  const mToFt = (m) => Math.round(m * 3.28084);
  const mToMi = (m) => (m / 1609.34).toFixed(1);
  const fmtDur = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  const stravaColor = "#FC4C02";
  const stravaBg = "linear-gradient(135deg,#1a0a05 0%,#2d1208 50%,#1a0a05 100%)";

  // Y-axis labels (min/mid/max elevation in ft)
  const yLabels = [
    { alt: maxAlt, y: yFor(maxAlt) },
    { alt: (maxAlt + minAlt) / 2, y: yFor((maxAlt + minAlt) / 2) },
    { alt: minAlt, y: yFor(minAlt) },
  ];

  // ── Segment color bands ──────────────────────────────────────────────────
  // Segments have durationSec from the Strava activity. Cumulate to get
  // startSec → endSec for each, then map to x-coordinates on the graph.
  const segmentColors = {
    "steep-climb":   "#FC4C02",
    "climb":         "#FC8B02",
    "flat":          "#1DB954",
    "descent":       "#4169E1",
    "steep-descent": "#7B4DFF",
  };
  let accSec = 0;
  const segmentBands = (ctx.segments || []).map(s => {
    const startSec = accSec;
    accSec += s.durationSec || 0;
    return {
      ...s,
      startSec,
      endSec: accSec,
      x1: xFor(minT + (startSec / tRange) * tRange),
      x2: xFor(minT + (accSec / tRange) * tRange),
    };
  });

  // ── Song markers (per-song playlist-fraction positions) ──────────────────
  const totalMs = queue.reduce((sum, t) => sum + (t.durationMs || 180000), 0);
  let accMs = 0;
  const songMarkers = queue.map((t, i) => {
    const startMs = accMs;
    accMs += (t.durationMs || 180000);
    const frac = totalMs > 0 ? startMs / totalMs : 0;
    return {
      index: i,
      track: t,
      x: PAD_L + frac * (W - PAD_L - PAD_R),
      fraction: frac,
      color: segmentColors[t.segmentKind] || stravaColor,
      isPast: i < queueIndex,
      isCurrent: i === queueIndex,
      isFuture: i > queueIndex,
    };
  });

  return (
    <div style={{ marginBottom: 18 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 4px 8px", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-block", width: 10, height: 10, borderRadius: 2,
            background: stravaColor,
            boxShadow: `0 0 10px ${stravaColor}`,
          }} />
          <span style={{
            fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase",
            color: "#fff", fontFamily: "monospace",
          }}>
            Ride Profile
          </span>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontFamily: "monospace" }}>
            · {ctx.activityType || "Activity"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontFamily: "monospace" }}>
            {Math.round(fraction * 100)}% through
          </span>
          <button
            onClick={toggleCollapsed}
            title={collapsed ? "Expand ride profile" : "Collapse ride profile"}
            style={{
              background: `${stravaColor}22`,
              border: `1px solid ${stravaColor}`,
              borderRadius: 6, padding: "3px 10px",
              color: stravaColor,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              fontFamily: "monospace", lineHeight: 1,
              boxShadow: `0 0 6px ${stravaColor}55`,
            }}
          >
            {collapsed ? "⌄" : "⌃"}
          </button>
        </div>
      </div>

      {/* Collapsed mini-bar — compact summary of ride progress when the
          full graph is hidden. Shows activity name, mini progress bar,
          miles ridden/total, and current elevation at marker. */}
      {collapsed && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "10px 14px", marginBottom: 10,
          background: stravaBg,
          border: `1px solid ${stravaColor}66`,
          borderRadius: 10,
        }}>
          {/* Left: small icon + activity name */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexShrink: 0, maxWidth: "40%" }}>
            <span style={{
              display: "inline-block", width: 14, height: 14, borderRadius: 3,
              background: stravaColor,
              boxShadow: `0 0 8px ${stravaColor}88`,
              flexShrink: 0,
            }} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 12, fontWeight: 700, color: "#fff",
                fontFamily: "'Playfair Display',serif",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                lineHeight: 1.2,
              }}>
                {displayTitle}
              </div>
              <div style={{
                fontSize: 9, color: "rgba(255,255,255,0.5)",
                fontFamily: "monospace", marginTop: 1,
              }}>
                {ctx.activityType || "Ride"}
              </div>
            </div>
          </div>

          {/* Middle: mini progress bar */}
          <div style={{ flex: 1, minWidth: 60, height: 4, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden", position: "relative" }}>
            <div style={{
              height: "100%",
              width: `${Math.min(100, fraction * 100)}%`,
              background: `linear-gradient(to right, ${stravaColor}, #FC8B02)`,
              boxShadow: `0 0 6px ${stravaColor}`,
              transition: "width 0.3s linear",
            }} />
          </div>

          {/* Right: compact stats */}
          <div style={{
            display: "flex", gap: 10, alignItems: "center", flexShrink: 0,
            fontSize: 10, fontFamily: "monospace", fontWeight: 700,
          }}>
            {ctx.totalDistanceMeters > 0 && (
              <span style={{ color: "#fff" }}>
                <span style={{ color: stravaColor }}>{(ctx.totalDistanceMeters / 1609.34 * fraction).toFixed(1)}</span>
                <span style={{ color: "rgba(255,255,255,0.35)" }}> / {(ctx.totalDistanceMeters / 1609.34).toFixed(1)} mi</span>
              </span>
            )}
            <span style={{ color: "rgba(255,255,255,0.7)" }}>
              {Math.round(currentAlt * 3.28084)}
              <span style={{ color: "rgba(255,255,255,0.4)" }}>ft</span>
            </span>
          </div>
        </div>
      )}

      {/* Tile */}
      {!collapsed && (
      <div style={{
        background: stravaBg,
        border: `2px solid ${stravaColor}`,
        borderRadius: 16,
        padding: "14px 16px",
        boxShadow: `0 0 0 1px ${stravaColor}33, 0 8px 24px rgba(252,76,2,0.25), inset 0 1px 0 rgba(255,255,255,0.06)`,
        position: "relative", overflow: "hidden",
      }}>
        {/* Activity name + stats */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          gap: 12, marginBottom: 10, position: "relative",
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 15, fontWeight: 700, color: "#fff",
              fontFamily: "'Playfair Display',serif", lineHeight: 1.2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {displayTitle}
            </div>
            {ctx.activityDate && (
              <div style={{
                fontSize: 10, color: "rgba(255,255,255,0.5)",
                fontFamily: "monospace", marginTop: 2,
              }}>
                {fmtDate(ctx.activityDate)}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <Stat label="Dist" value={ctx.totalDistanceMeters ? mToMi(ctx.totalDistanceMeters) : "—"} unit="mi" color={stravaColor} />
            <Stat label="Gain" value={ctx.elevationGainMeters ? mToFt(ctx.elevationGainMeters) : "—"} unit="ft" color={stravaColor} />
            <Stat label="Time" value={fmtDur(ctx.totalDurationSec || 0)} unit="" color={stravaColor} />
          </div>
        </div>

        {/* SVG elevation graph (with tooltip overlay) */}
        <div style={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: 160, display: "block" }}
        >
          {/* Segment color bands (behind everything) */}
          {segmentBands.map((s, i) => {
            const c = segmentColors[s.kind] || stravaColor;
            return (
              <rect
                key={`seg-${i}`}
                x={s.x1} y={PAD_T}
                width={Math.max(0, s.x2 - s.x1)}
                height={H - PAD_T - PAD_B}
                fill={c} opacity="0.08"
              />
            );
          })}
          {/* Segment dividers */}
          {segmentBands.map((s, i) => (
            i > 0 ? (
              <line key={`segdiv-${i}`}
                x1={s.x1} y1={PAD_T} x2={s.x1} y2={H - PAD_B}
                stroke="rgba(255,255,255,0.06)" strokeWidth="1"
              />
            ) : null
          ))}

          {/* Y-axis labels + gridlines */}
          {yLabels.map((y, i) => (
            <g key={i}>
              <line
                x1={PAD_L} y1={y.y} x2={W - PAD_R} y2={y.y}
                stroke="rgba(255,255,255,0.08)" strokeDasharray="2 3" strokeWidth="1"
              />
              <text
                x={PAD_L - 4} y={y.y + 3} textAnchor="end"
                fill="rgba(255,255,255,0.45)" fontSize="9" fontFamily="monospace"
              >
                {mToFt(y.alt)}ft
              </text>
            </g>
          ))}
          {/* Gradients */}
          <defs>
            <linearGradient id="elevGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stravaColor} stopOpacity="0.5" />
              <stop offset="100%" stopColor={stravaColor} stopOpacity="0.03" />
            </linearGradient>
            {/* "Ridden" overlay — dark fade from start → current marker so
                the upcoming portion stands out. Darker at the far left
                (longest-ago) lightening toward the current position. */}
            <linearGradient id="riddenOverlay" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#000" stopOpacity="0.55" />
              <stop offset="80%" stopColor="#000" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#000" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Filled area under curve */}
          <path d={fillPath} fill="url(#elevGradient)" />
          {/* Dim the "ridden" portion — applied after the fill but before the
              top line so the elevation curve itself stays crisp. */}
          {markerX > PAD_L + 1 && (
            <rect
              x={PAD_L} y={PAD_T}
              width={Math.max(0, markerX - PAD_L)}
              height={H - PAD_T - PAD_B}
              fill="url(#riddenOverlay)"
            />
          )}
          {/* Line on top */}
          <path d={linePath} fill="none" stroke={stravaColor} strokeWidth="2" strokeLinejoin="round" />

          {/* Song markers — placed at each song's playlist-fraction x */}
          {songMarkers.map(m => {
            const baseR = m.isCurrent ? 7 : 4;
            const strokeW = m.isCurrent ? 2.5 : 1.5;
            const opacity = m.isPast ? 0.35 : 1;
            const isSelected = selectedSong?.index === m.index;
            return (
              <g
                key={`sm-${m.index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle: tap selects, tap again deselects. Tap on a
                  // different marker reassigns selection. Works on touch.
                  setSelectedSong(prev => (prev?.index === m.index ? null : m));
                }}
                style={{ cursor: "pointer" }}
              >
                {/* Larger invisible hit area — important for touch devices */}
                <circle cx={m.x} cy={H - PAD_B - 6} r="14" fill="transparent" />
                {/* Visible dot */}
                <circle
                  cx={m.x} cy={H - PAD_B - 6} r={isSelected ? baseR + 2 : baseR}
                  fill={m.color} stroke="#fff" strokeWidth={isSelected ? strokeW + 1 : strokeW}
                  opacity={opacity}
                  style={(m.isCurrent || isSelected) ? { filter: `drop-shadow(0 0 6px ${m.color})` } : {}}
                />
                {m.isCurrent && (
                  <circle cx={m.x} cy={H - PAD_B - 6} r={baseR + 3}
                    fill="none" stroke={m.color} strokeWidth="1.5" opacity="0.6">
                    <animate attributeName="r" values={`${baseR + 3};${baseR + 8};${baseR + 3}`} dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.6;0;0.6" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Progress line + marker (on the curve) */}
          <line
            x1={markerX} y1={PAD_T - 4}
            x2={markerX} y2={H - PAD_B}
            stroke="#fff" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.8"
          />
          <circle cx={markerX} cy={markerY} r="7" fill="#fff" stroke={stravaColor} strokeWidth="2" />
          <circle cx={markerX} cy={markerY} r="3" fill={stravaColor} />

          {/* X-axis endpoints */}
          <text x={PAD_L} y={H - 4} textAnchor="start" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="monospace">0</text>
          <text x={W - PAD_R} y={H - 4} textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="monospace">{fmtDur(ctx.totalDurationSec || 0)}</text>
        </svg>

        {/* Tap-activated tooltip (positioned absolutely over SVG by fraction).
            Works on both desktop and touch devices. */}
        {selectedSong && (
          <div style={{
            position: "absolute",
            left: `${(selectedSong.x / W) * 100}%`,
            top: -4,
            transform: selectedSong.fraction > 0.75 ? "translateX(-100%)" : selectedSong.fraction < 0.25 ? "translateX(0)" : "translateX(-50%)",
            background: "rgba(10,16,37,0.97)",
            border: `1px solid ${selectedSong.color}`,
            borderRadius: 8, padding: "8px 10px",
            minWidth: 220, maxWidth: 300,
            boxShadow: `0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px ${selectedSong.color}44`,
            zIndex: 10,
          }}>
            {/* Header row: segment/position + close button */}
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
              color: selectedSong.color, fontFamily: "monospace", marginBottom: 4,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6,
            }}>
              <span>
                {selectedSong.isCurrent ? "▶ NOW" : selectedSong.isPast ? "PLAYED" : "UPCOMING"}
                <span style={{ color: "rgba(255,255,255,0.5)", marginLeft: 6 }}>#{selectedSong.index + 1}</span>
              </span>
              <button
                onClick={() => setSelectedSong(null)}
                title="Close"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 4, width: 20, height: 20,
                  color: "rgba(255,255,255,0.7)", fontSize: 10,
                  cursor: "pointer", padding: 0, lineHeight: 1,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700, color: "#fff",
              fontFamily: "'Playfair Display',serif", lineHeight: 1.2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {selectedSong.track.name}
            </div>
            <div style={{
              fontSize: 10, color: "rgba(255,255,255,0.65)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              marginTop: 1,
            }}>
              {selectedSong.track.artists}
            </div>
            {selectedSong.track.segmentLabel && (
              <div style={{
                fontSize: 10, color: selectedSong.color,
                fontFamily: "monospace", fontWeight: 700, marginTop: 6,
              }}>
                {selectedSong.track.segmentLabel}
              </div>
            )}
            {selectedSong.track.reasoning && (
              <div style={{
                fontSize: 10, color: "rgba(255,255,255,0.6)",
                marginTop: 4, fontStyle: "italic", lineHeight: 1.35,
              }}>
                💭 {selectedSong.track.reasoning}
              </div>
            )}
            {/* Jump button — only for future (upcoming) songs */}
            {selectedSong.isFuture && (
              <button
                onClick={() => {
                  jumpToIndex(selectedSong.index);
                  setSelectedSong(null);
                }}
                style={{
                  marginTop: 8, width: "100%",
                  background: selectedSong.color,
                  border: "none", borderRadius: 6, padding: "6px 10px",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                  cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                  boxShadow: `0 2px 6px ${selectedSong.color}88`,
                }}
              >
                ▶ Jump to this song
              </button>
            )}
          </div>
        )}
        </div>

        {/* Mileage strip — aligned to graph x-axis so marker positions match */}
        {ctx.totalDistanceMeters > 0 && (() => {
          const totalMi = ctx.totalDistanceMeters / 1609.34;
          const currentMi = totalMi * fraction;
          const remainingMi = Math.max(0, totalMi - currentMi);
          // Pick a tick interval that yields 4–8 ticks
          const tickEvery = totalMi <= 5 ? 1 : totalMi <= 15 ? 2 : totalMi <= 30 ? 5 : 10;
          const ticks = [];
          for (let m = 0; m <= totalMi; m += tickEvery) ticks.push(m);
          if (ticks[ticks.length - 1] < totalMi - 0.01) ticks.push(totalMi);
          // Use the same inner-graph percentages as the SVG (PAD_L=34, PAD_R=10 of W=560)
          const leftInsetPct = (PAD_L / W) * 100;
          const rightInsetPct = (PAD_R / W) * 100;
          const innerPct = 100 - leftInsetPct - rightInsetPct;
          const mileToLeftPct = (mi) => leftInsetPct + (mi / totalMi) * innerPct;

          return (
            <div style={{ marginTop: 8, position: "relative" }}>
              {/* Section label */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                fontSize: 10, color: "rgba(255,255,255,0.55)", fontFamily: "monospace",
                marginBottom: 6, padding: "0 4px",
              }}>
                <span style={{ fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: stravaColor }}>
                  {currentMi.toFixed(1)} mi ridden
                </span>
                <span style={{ fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {remainingMi.toFixed(1)} mi to go
                </span>
              </div>

              {/* Progress bar */}
              <div style={{ position: "relative", height: 8, marginBottom: 4 }}>
                {/* Track (unridden portion) */}
                <div style={{
                  position: "absolute",
                  left: `${leftInsetPct}%`, right: `${rightInsetPct}%`,
                  top: 0, height: "100%",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 4,
                }} />
                {/* Filled (ridden portion) */}
                <div style={{
                  position: "absolute",
                  left: `${leftInsetPct}%`,
                  width: `${fraction * innerPct}%`,
                  top: 0, height: "100%",
                  background: `linear-gradient(to right, ${stravaColor}, #FC8B02)`,
                  borderRadius: 4,
                  boxShadow: `0 0 8px ${stravaColor}55`,
                  transition: "width 0.3s linear",
                }} />
                {/* Current position marker — inverted triangle above the bar */}
                <div style={{
                  position: "absolute",
                  left: `${mileToLeftPct(currentMi)}%`,
                  top: -3,
                  transform: "translateX(-50%)",
                  width: 0, height: 0,
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: `6px solid #fff`,
                  filter: `drop-shadow(0 0 4px ${stravaColor})`,
                }} />
              </div>

              {/* Mile tick marks + labels */}
              <div style={{ position: "relative", height: 14, marginTop: 2 }}>
                {ticks.map((mi, i) => {
                  const leftPct = mileToLeftPct(mi);
                  return (
                    <div key={i} style={{
                      position: "absolute",
                      left: `${leftPct}%`,
                      transform: "translateX(-50%)",
                      display: "flex", flexDirection: "column", alignItems: "center",
                    }}>
                      <div style={{
                        width: 1, height: 4,
                        background: "rgba(255,255,255,0.25)",
                      }} />
                      <span style={{
                        fontSize: 9, fontFamily: "monospace",
                        color: "rgba(255,255,255,0.45)",
                        marginTop: 1,
                      }}>
                        {mi === 0 ? "0" : mi >= totalMi - 0.01 ? `${totalMi.toFixed(1)}` : `${mi}`}
                      </span>
                    </div>
                  );
                })}
                {/* "mi" unit label on the right */}
                <span style={{
                  position: "absolute", right: 4, top: 0,
                  fontSize: 9, fontFamily: "monospace",
                  color: "rgba(255,255,255,0.35)",
                }}>
                  mi
                </span>
              </div>
            </div>
          );
        })()}

        {/* Segment color legend */}
        <div style={{
          display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center",
          marginTop: 10, fontSize: 9, fontFamily: "monospace",
          color: "rgba(255,255,255,0.5)",
        }}>
          {[
            ["steep-climb", "Steep Climb"],
            ["climb", "Climb"],
            ["flat", "Flat"],
            ["descent", "Descent"],
            ["steep-descent", "Fast Descent"],
          ].map(([k, label]) => {
            const used = segmentBands.some(s => s.kind === k);
            if (!used) return null;
            return (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, background: segmentColors[k], borderRadius: 2, display: "inline-block" }} />
                {label}
              </div>
            );
          })}
        </div>

        {/* Caption */}
        <div style={{
          fontSize: 10, color: "rgba(255,255,255,0.45)",
          fontFamily: "monospace", textAlign: "center", marginTop: 6,
        }}>
          Position synced to playlist progress · {Math.round(currentAlt * 3.28084)}ft at marker
        </div>
      </div>
      )}
    </div>
  );
}

// Small stat pill used in the elevation graph header
function Stat({ label, value, unit, color }) {
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{
        fontSize: 8, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
        color: "rgba(255,255,255,0.4)", fontFamily: "monospace",
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, fontWeight: 700, color,
        fontFamily: "monospace", lineHeight: 1,
      }}>
        {value}<span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginLeft: 2 }}>{unit}</span>
      </div>
    </div>
  );
}

// ─── PlaylistLibrary Modal ──────────────────────────────────────────────────
// Modal for browsing previously generated AI playlists. Split into Favorites
// and Recent. Each row: ▶ Play, ♥ Favorite toggle, 🗑 Delete. Avoids re-running
// the Claude API generation if a prior playlist is good enough.
function PlaylistLibrary({ player, onClose }) {
  if (!player) return null;
  const { library, toggleFavorite, deleteFromLibrary, playFromLibrary } = player;

  const favorites = library.filter(e => e.favorited)
    .sort((a, b) => a.name.localeCompare(b.name));
  const recents = library.filter(e => !e.favorited)
    .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));

  const color = "#4169E1";

  const renderRow = (entry) => (
    <div key={entry.id} style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 12px", marginBottom: 6,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: "#fff",
          fontFamily: "'Playfair Display',serif",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {entry.name || "Untitled playlist"}
        </div>
        <div style={{
          fontSize: 10, color: "rgba(255,255,255,0.45)", fontFamily: "monospace", marginTop: 2,
        }}>
          {entry.tracks?.length || 0} tracks · {ago(entry.savedAt)} · {dateTime(entry.savedAt)}
        </div>
      </div>
      <button
        onClick={() => toggleFavorite(entry.id)}
        title={entry.favorited ? "Remove from favorites" : "Add to favorites"}
        style={{
          background: entry.favorited ? "rgba(252,76,2,0.15)" : "rgba(255,255,255,0.06)",
          border: `1px solid ${entry.favorited ? "rgba(252,76,2,0.5)" : "rgba(255,255,255,0.12)"}`,
          borderRadius: 6, padding: "6px 8px", cursor: "pointer",
          fontSize: 13, lineHeight: 1, flexShrink: 0,
        }}
      >
        {entry.favorited ? "❤️" : "🤍"}
      </button>
      <button
        onClick={async () => { await playFromLibrary(entry.id); onClose(); }}
        title="Play in Figjam"
        style={{
          background: color, border: "none",
          borderRadius: 6, padding: "6px 12px", cursor: "pointer",
          color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0,
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        ▶ Play
      </button>
      <button
        onClick={() => {
          if (window.confirm(`Delete "${entry.name}" from your library?`)) {
            deleteFromLibrary(entry.id);
          }
        }}
        title="Delete"
        style={{
          background: "transparent", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 6, padding: "6px 8px", cursor: "pointer",
          color: "rgba(255,255,255,0.5)", fontSize: 12, lineHeight: 1, flexShrink: 0,
        }}
      >
        🗑
      </button>
    </div>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, zIndex: 100,
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "linear-gradient(135deg,#0a1025 0%,#0f1a3d 60%,#0a1025 100%)",
          border: `1px solid ${color}55`,
          borderRadius: 16, padding: "20px 22px",
          width: "100%", maxWidth: 600, maxHeight: "85vh",
          boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px ${color}22`,
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{
            margin: 0, fontSize: 22, fontFamily: "'Playfair Display',serif",
            color: "#fff", fontWeight: 700,
          }}>
            📚 Playlist Library
          </h2>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 8, width: 32, height: 32, color: "#fff",
            cursor: "pointer", fontSize: 14, lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Persistent library-full banner — only shown when non-favorited
            count is at or above the cap. Bold red, stays visible until the
            user deletes/favorites enough entries to free up a slot, so the
            cap state is unmistakable when entering from a gated builder. */}
        {recents.length >= FIGJAM_LIBRARY_CAP && (
          <div style={{
            marginBottom: 14, padding: "14px 16px",
            background: "rgba(220,38,38,0.12)",
            border: "2px solid #DC2626",
            borderRadius: 10,
            fontFamily: "'DM Sans',sans-serif",
            animation: "figjamLivePulse 2s ease-in-out infinite",
          }}>
            <div style={{
              fontSize: 14, fontWeight: 900, color: "#FF3B30",
              letterSpacing: "0.04em", marginBottom: 4, textTransform: "uppercase",
            }}>
              ⚠️ Playlist Library Full ({recents.length} / {FIGJAM_LIBRARY_CAP})
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#FF6B6B", lineHeight: 1.4 }}>
              Delete a playlist below to make room — or tap 🤍 to ♥ favorite the ones you want to keep (favorites don't count against the limit).
            </div>
          </div>
        )}

        <div style={{ overflowY: "auto", flex: 1, marginRight: -6, paddingRight: 6 }}>
          {library.length === 0 && (
            <div style={{
              padding: "40px 20px", textAlign: "center",
              color: "rgba(255,255,255,0.5)", fontSize: 13,
            }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
              No playlists saved yet. Generate an AI playlist from a ride to start building your library.
            </div>
          )}

          {favorites.length > 0 && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase",
                color: "#FC4C02", fontFamily: "monospace", marginBottom: 8, marginTop: 4,
              }}>
                ❤️ Favorites ({favorites.length})
              </div>
              {favorites.map(renderRow)}
            </>
          )}

          {recents.length > 0 && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.5)", fontFamily: "monospace",
                marginBottom: 8, marginTop: favorites.length > 0 ? 16 : 4,
              }}>
                Recent ({recents.length})
              </div>
              {recents.map(renderRow)}
            </>
          )}
        </div>

        <div style={{
          fontSize: 10, color: "rgba(255,255,255,0.35)",
          fontFamily: "monospace", textAlign: "center", marginTop: 14,
        }}>
          Favorites are kept forever · Recent capped at {FIGJAM_LIBRARY_CAP} entries
        </div>
      </div>
    </div>
  );
}

// ─── Spotify Player Test Modal ──────────────────────────────────────────────
// Isolated test harness for verifying Spotify Web Playback SDK works end-to-end.
// 8 independent steps — each button tests one thing. Self-contained; does not
// touch timeline/now-playing/any main app state.
function SpotifyPlayerTest({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [steps, setSteps] = useState({
    1: "pending", 2: "pending", 3: "pending", 4: "pending",
    5: "pending", 6: "pending", 7: "pending", 8: "pending",
  });
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [token, setToken] = useState(null);
  const [testTracks, setTestTracks] = useState([]);

  const log = (msg) => {
    const ts = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${ts}] ${msg}`]);
  };
  const mark = (n, status) => setSteps(s => ({ ...s, [n]: status }));

  // Step 1: Verify SDK loaded
  const test1 = () => {
    log("Step 1: Checking window.Spotify…");
    if (window.Spotify && window.Spotify.Player) {
      log("✓ window.Spotify.Player exists");
      mark(1, "pass");
    } else {
      log("✗ window.Spotify NOT found — SDK didn't load");
      mark(1, "fail");
    }
  };

  // Step 2: Fetch token from server
  const test2 = async () => {
    log("Step 2: Fetching token from /api/spotify-token…");
    try {
      const res = await fetch("/api/spotify-token");
      if (!res.ok) {
        log(`✗ HTTP ${res.status} — ${res.status === 401 ? "not connected to Spotify" : "endpoint error"}`);
        mark(2, "fail");
        return;
      }
      const data = await res.json();
      if (!data.accessToken) { log("✗ No accessToken in response"); mark(2, "fail"); return; }
      log(`✓ Got token (${data.accessToken.slice(0, 12)}…)`);
      setToken(data.accessToken);
      mark(2, "pass");
    } catch (e) {
      log(`✗ Fetch error: ${e.message}`);
      mark(2, "fail");
    }
  };

  // Step 3: Create player instance
  const test3 = () => {
    log("Step 3: Creating Spotify.Player instance…");
    if (!token) { log("✗ No token — run Step 2 first"); mark(3, "fail"); return; }
    if (!window.Spotify) { log("✗ SDK not loaded — run Step 1 first"); mark(3, "fail"); return; }
    try {
      const p = new window.Spotify.Player({
        name: "Figjam Test Player",
        getOAuthToken: (cb) => cb(token),
        volume: 0.5,
      });
      p.addListener("ready", ({ device_id }) => { log(`✓ ready event fired, device_id: ${device_id.slice(0, 12)}…`); setDeviceId(device_id); });
      p.addListener("not_ready", () => log("⚠ device went offline"));
      p.addListener("initialization_error", ({ message }) => log(`✗ init_error: ${message}`));
      p.addListener("authentication_error", ({ message }) => log(`✗ auth_error: ${message} — reconnect Spotify`));
      p.addListener("account_error", ({ message }) => log(`✗ account_error: ${message} — PREMIUM REQUIRED`));
      p.addListener("playback_error", ({ message }) => log(`✗ playback_error: ${message}`));
      setPlayer(p);
      log("✓ Player instance created (listeners attached)");
      mark(3, "pass");
    } catch (e) {
      log(`✗ Create error: ${e.message}`);
      mark(3, "fail");
    }
  };

  // Step 4: Connect player (triggers ready event)
  // Uses a Promise-based listener + 15s timeout with progress logging.
  // Fixes stale-closure issue with the old polling approach.
  const test4 = async () => {
    log("Step 4: Calling player.connect()…");
    if (!player) { log("✗ No player — run Step 3 first"); mark(4, "fail"); return; }

    // Already connected? (ready event already fired from Step 3)
    if (deviceId) {
      log(`✓ device_id already received: ${deviceId.slice(0, 12)}…`);
      mark(4, "pass");
      return;
    }

    try {
      // Set up a one-shot ready listener BEFORE calling connect
      const readyPromise = new Promise((resolve, reject) => {
        let elapsed = 0;
        const progressTimer = setInterval(() => {
          elapsed += 2;
          log(`  …still waiting (${elapsed}s)`);
        }, 2000);

        const timeoutTimer = setTimeout(() => {
          clearInterval(progressTimer);
          try { player.removeListener("ready", handler); } catch {}
          reject(new Error("Timed out after 15s — check browser console for Spotify errors"));
        }, 15000);

        const handler = ({ device_id }) => {
          clearInterval(progressTimer);
          clearTimeout(timeoutTimer);
          try { player.removeListener("ready", handler); } catch {}
          resolve(device_id);
        };

        player.addListener("ready", handler);
      });

      const connected = await player.connect();
      if (!connected) {
        log("✗ connect() returned false — usually means already connected or SDK issue");
        mark(4, "fail");
        return;
      }
      log("✓ connect() returned true — waiting for ready event (up to 15s)…");

      const devId = await readyPromise;
      log(`✓ device_id received: ${devId.slice(0, 12)}…`);
      setDeviceId(devId);
      mark(4, "pass");
    } catch (e) {
      log(`✗ ${e.message}`);
      mark(4, "fail");
    }
  };

  // Step 5: Transfer playback to our device
  const test5 = async () => {
    log("Step 5: Transferring playback to Figjam device…");
    if (!deviceId || !token) { log("✗ Missing deviceId or token"); mark(5, "fail"); return; }
    try {
      const res = await fetch("https://api.spotify.com/v1/me/player", {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ device_ids: [deviceId], play: false }),
      });
      if (res.status === 204 || res.status === 202) {
        log(`✓ Transfer succeeded (${res.status}) — check Spotify app for "Figjam Test Player"`);
        mark(5, "pass");
      } else {
        const body = await res.text();
        log(`✗ HTTP ${res.status}: ${body.slice(0, 200)}`);
        mark(5, "fail");
      }
    } catch (e) {
      log(`✗ Transfer error: ${e.message}`);
      mark(5, "fail");
    }
  };

  // Step 6: Play one test track (popular known track)
  const test6 = async () => {
    log("Step 6: Playing one test track (The Weeknd — Blinding Lights)…");
    if (!deviceId || !token) { log("✗ Missing deviceId or token"); mark(6, "fail"); return; }
    try {
      const testUri = "spotify:track:0VjIjW4GlUZAMYd2vXMi3b"; // Blinding Lights
      const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ uris: [testUri] }),
      });
      if (res.status === 204 || res.status === 202) {
        log("✓ Play command accepted — audio should start in a moment");
        mark(6, "pass");
      } else {
        const body = await res.text();
        log(`✗ HTTP ${res.status}: ${body.slice(0, 200)}`);
        mark(6, "fail");
      }
    } catch (e) {
      log(`✗ Play error: ${e.message}`);
      mark(6, "fail");
    }
  };

  // Step 7: Play a queue of 3 tracks
  const test7 = async () => {
    log("Step 7: Playing a queue of 3 tracks…");
    if (!deviceId || !token) { log("✗ Missing deviceId or token"); mark(7, "fail"); return; }
    try {
      const uris = [
        "spotify:track:0VjIjW4GlUZAMYd2vXMi3b", // Blinding Lights
        "spotify:track:7qiZfU4dY1lWllzX7mPBI3", // Shape of You
        "spotify:track:11dFghVXANMlKmJXsNCbNl", // Cut to the Feeling
      ];
      const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ uris }),
      });
      if (res.status === 204 || res.status === 202) {
        log("✓ Queue started — songs will auto-advance");
        setTestTracks(uris);
        mark(7, "pass");
      } else {
        const body = await res.text();
        log(`✗ HTTP ${res.status}: ${body.slice(0, 200)}`);
        mark(7, "fail");
      }
    } catch (e) {
      log(`✗ Queue error: ${e.message}`);
      mark(7, "fail");
    }
  };

  // Step 8: Skip / Pause / Resume tests
  const test8Action = async (action) => {
    if (!player) { log("✗ No player"); return; }
    try {
      if (action === "pause") { await player.pause(); log("✓ pause() called"); }
      else if (action === "resume") { await player.resume(); log("✓ resume() called"); }
      else if (action === "next") { await player.nextTrack(); log("✓ nextTrack() called"); }
      else if (action === "prev") { await player.previousTrack(); log("✓ previousTrack() called"); }
      mark(8, "pass");
    } catch (e) {
      log(`✗ ${action} error: ${e.message}`);
      mark(8, "fail");
    }
  };

  // Cleanup on close
  const handleClose = async () => {
    if (player) {
      try { await player.disconnect(); log("Player disconnected"); } catch {}
    }
    onClose();
  };

  const statusIcon = (s) => s === "pass" ? "✅" : s === "fail" ? "❌" : "⚪";
  const statusColor = (s) => s === "pass" ? "#1DB954" : s === "fail" ? "#FC4C02" : "rgba(255,255,255,0.3)";

  const stepDefs = [
    { n: 1, label: "SDK Loaded", run: test1, desc: "Verifies window.Spotify is available" },
    { n: 2, label: "Fetch Token", run: test2, desc: "Gets Spotify access token from server" },
    { n: 3, label: "Create Player", run: test3, desc: "Instantiates Spotify.Player" },
    { n: 4, label: "Connect Player", run: test4, desc: "Calls connect(), waits for ready + device_id" },
    { n: 5, label: "Transfer Playback", run: test5, desc: "Makes this device active in Spotify" },
    { n: 6, label: "Play Test Track", run: test6, desc: "Plays a single hardcoded song" },
    { n: 7, label: "Play Queue", run: test7, desc: "Plays 3 songs in sequence" },
    { n: 8, label: "Controls", run: null, desc: "Skip / pause / resume via SDK methods" },
  ];

  return (
    <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 260, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: 20, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0c1016", border: "1px solid rgba(65,105,225,0.35)", borderRadius: 16, width: "100%", maxWidth: 640, padding: 24, marginTop: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontFamily: "'Playfair Display',serif", color: "#4169E1" }}>🧪 Spotify Player Test</h2>
          <button onClick={handleClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 22 }}>✕</button>
        </div>

        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 14, lineHeight: 1.5 }}>
          Isolated test for Spotify Web Playback SDK. Run each step in order. Requires Spotify Premium + reconnect with the <code style={{ color: "#4169E1" }}>streaming</code> scope.
        </div>

        {/* Test buttons */}
        <div style={{ marginBottom: 12 }}>
          {stepDefs.map(({ n, label, run, desc }) => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
              <span style={{ fontSize: 14, color: statusColor(steps[n]), width: 20 }}>{statusIcon(steps[n])}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>Step {n}: {label}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{desc}</div>
              </div>
              {n === 8 ? (
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => test8Action("prev")} style={testBtnSmall}>⏮</button>
                  <button onClick={() => test8Action("pause")} style={testBtnSmall}>⏸</button>
                  <button onClick={() => test8Action("resume")} style={testBtnSmall}>▶</button>
                  <button onClick={() => test8Action("next")} style={testBtnSmall}>⏭</button>
                </div>
              ) : (
                <button onClick={run} style={{ background: "rgba(65,105,225,0.2)", border: "1px solid #4169E1", color: "#4169E1", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                  Run
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Log output */}
        <div style={{ background: "#000", borderRadius: 8, padding: 10, maxHeight: 180, overflowY: "auto", fontFamily: "monospace", fontSize: 10, color: "rgba(255,255,255,0.7)" }}>
          {logs.length === 0 ? (
            <div style={{ color: "rgba(255,255,255,0.3)" }}>Log output will appear here…</div>
          ) : (
            logs.map((l, i) => <div key={i} style={{ marginBottom: 2 }}>{l}</div>)
          )}
        </div>
        <button onClick={() => setLogs([])} style={{ marginTop: 6, background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 10, cursor: "pointer" }}>Clear log</button>
      </div>
    </div>
  );
}

const testBtnSmall = {
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#fff",
  borderRadius: 6,
  width: 28, height: 28,
  fontSize: 11,
  cursor: "pointer",
};

function RidePlaylistBuilder({ onClose, player, supabaseSession, onOpenLibrary }) {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAct, setSelectedAct] = useState(null); // full activity object for context
  // The user supplies the front half of the playlist name; the date and
  // "Figjam Playlist" suffix are fixed/appended automatically.
  const [customName, setCustomName] = useState("");
  const todayStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const trimmedCustom = (customName || "").trim();
  const fullPlaylistName = trimmedCustom
    ? `${trimmedCustom} · ${todayStr} · Figjam Playlist`
    : "";
  const [description, setDescription] = useState("");
  // Free-text guidance the user can give Claude for the curation ("more Tool
  // on climbs, jam-band music on descents", etc.). Optional; passed as
  // ridePreferences.notes to the AI.
  const [userNotes, setUserNotes] = useState("");
  // Variety mode — user must explicitly pick before generating.
  // Drives the per-artist song caps in the AI playlist builder.
  const [varietyMode, setVarietyMode] = useState(null);

  // Recommend a variety mode based on what's in userNotes:
  //   "favorites" — when the prompt names multiple artists (long detailed
  //                 prompt with proper-noun phrases) → fills longer rides
  //                 by allowing 3 songs each from named artists
  //   "discover"  — when the prompt is broad/generic or explicitly mentions
  //                 discovery → max variety, many unique artists
  //   "balanced"  — neutral default for short or genre-only prompts
  const recommendedMode = (() => {
    if (!userNotes || userNotes.trim().length < 30) return "balanced";
    const lower = userNotes.toLowerCase();
    if (/\b(discover|explore|new music|new song|surprise me|new to me)\b/.test(lower)) return "discover";
    // Count distinct multi-word capitalized phrases — proxy for artist names
    const capitalizedPhrases = userNotes.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3}/g) || [];
    // De-duplicate
    const uniqueCount = new Set(capitalizedPhrases.map(s => s.toLowerCase())).size;
    if (uniqueCount >= 3) return "favorites";
    return "balanced";
  })();
  // Discovery mode: "library" (current Liked + top only, fast) vs.
  // "explore" (also pulls deep cuts from artists in library + followed)
  const [discoveryMode, setDiscoveryMode] = useState("library");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Progress state for the async swarm path. Shape: { completed, total, mode }
  // - mode === "swarm" → show "X of Y segments complete" + progress bar
  // - mode === "single" → no segment count, just a generic "generating" state
  const [genProgress, setGenProgress] = useState(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);

  // Load activities on open. When signed in via Supabase, use the v2
  // endpoint that reads Strava tokens from Postgres per-user. Falls back
  // to the legacy single-user endpoint otherwise.
  useEffect(() => {
    let cancelled = false;
    const fetchActivities = async () => {
      const url = supabaseSession?.access_token
        ? "/api/v2/ride-activities"
        : "/api/ride-activities";
      const headers = supabaseSession?.access_token
        ? { Authorization: `Bearer ${supabaseSession.access_token}` }
        : {};
      try {
        const r = await fetch(url, { headers });
        const data = await r.json();
        if (cancelled) return;
        if (Array.isArray(data)) {
          setActivities(data.filter(a => ["Ride", "Run", "VirtualRide", "MountainBikeRide", "GravelRide", "Hike"].includes(a.type)));
        } else if (data.error) {
          setError(data.error);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoadingActivities(false);
      }
    };
    fetchActivities();
    return () => { cancelled = true; };
  }, [supabaseSession?.access_token]);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const fmtMi = (m) => (m / 1609.34).toFixed(1) + " mi";
  const fmtTime = (s) => `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;

  const handleSelect = (act) => {
    setSelectedId(act.id);
    setSelectedAct(act);
    // Pre-fill with just the activity name — user can edit this, and the
    // date + "Figjam Playlist" suffix are appended automatically.
    setCustomName(act.name || "My Ride");
    setDescription(`${fmtMi(act.distance)} · ${Math.round(act.elevationGain * 3.28084)} ft · Generated by Figjam`);
    setPreview(null);
    setCreated(null);
  };

  const handlePreview = async () => {
    if (!selectedId) return;
    setPreviewing(true);
    setError(null);
    try {
      // Collect URIs from ALL previously-generated playlists in the library
      // so the AI avoids reusing any song until the candidate pool gets too
      // small (the backend has a MIN_POOL safety net). The bigger the user's
      // library + the more history we exclude, the more ride-to-ride variety.
      const excludeUris = [];
      if (player?.library?.length) {
        const seen = new Set();
        player.library.forEach(entry => {
          (entry.tracks || []).forEach(t => {
            if (t.uri && !seen.has(t.uri)) { seen.add(t.uri); excludeUris.push(t.uri); }
          });
        });
      }
      // When signed in via Supabase, use the v2 multi-user endpoint that
      // reads the user's Spotify token from Postgres. Falls back to the
      // legacy single-user endpoint if not signed in.
      const useV2 = !!supabaseSession?.access_token;
      // Bypass Vercel's edge proxy for this endpoint by calling Render
      // directly in production — Vercel's Hobby tier edge times out
      // proxied requests around 30s, but playlist generation can take
      // 60-150s (especially in Explore mode). CORS is already allowed
      // for getfigjam.app on the backend. In dev (localhost) we keep
      // the relative path so CRA's proxy + cookies still work.
      const apiBase = (typeof window !== "undefined" && window.location.hostname !== "localhost")
        ? "https://figjam-backend.onrender.com"
        : "";
      const path = useV2 ? "/api/v2/preview-ride-playlist" : "/api/preview-ride-playlist";
      const url = `${apiBase}${path}`;
      const headers = { "Content-Type": "application/json" };
      if (useV2) headers.Authorization = `Bearer ${supabaseSession.access_token}`;
      setGenProgress(null);
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          activityId: selectedId,
          excludeUris,
          ridePreferences: userNotes.trim() ? { notes: userNotes.trim() } : undefined,
          // Pull Figjam-side discovered pool + dislike list so the AI can
          // use them / avoid them respectively.
          figjamTracks: player?.discoveredTracks || [],
          dislikedUris: player?.notJams ? [...player.notJams] : [],
          discoveryMode,
          varietyMode, // user-chosen: discover / balanced / favorites
          // Send the matched preset label so the backend can decide whether
          // to use the swarm engine (Explore mode OR a preset selected =
          // deep search). Backend uses presence/absence, not the exact value.
          preset: matchPresetLabel(userNotes.trim()) || "",
        }),
      });
      let data = await res.json();

      // The v2 endpoint now returns 202 + {jobId, status, poll} and runs
      // generation in the background. Poll until status is "done" (then
      // use job.result as the data) or "error". The legacy v1 endpoint
      // (signed-out / dev path) still returns the full result synchronously.
      if (useV2 && data.jobId && !data.playlist) {
        const jobId = data.jobId;
        const POLL_INTERVAL_MS = 1500;
        const MAX_POLL_MS = 6 * 60 * 1000; // 6 min safety cap (swarm builds for long rides can run 3-5 min)
        const startedAt = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (Date.now() - startedAt > MAX_POLL_MS) {
            throw new Error("Generation timed out — please try again");
          }
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          const pollRes = await fetch(`${apiBase}/api/v2/playlist-jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${supabaseSession.access_token}` },
          });
          if (!pollRes.ok) {
            // Transient network error — keep polling a few more times before giving up.
            continue;
          }
          const pollData = await pollRes.json();
          // Update progress UI as workers complete
          setGenProgress({
            completed: pollData.completedSegments || 0,
            total: pollData.totalSegments || 0,
            mode: pollData.mode || null,
          });
          if (pollData.status === "done" && pollData.result) {
            data = pollData.result;
            break;
          }
          if (pollData.status === "error") {
            throw new Error(pollData.error || "Generation failed");
          }
          // pending | running → keep polling
        }
      }

      if (data.error) {
        setError(data.error);
      } else {
        setPreview(data);
        // Capture any discovered (non-library) tracks into Figjam's pool so
        // they're available for future generations without re-searching.
        player?.captureDiscoveredTracks?.(data.playlist);
        // Auto-save this generated playlist to the library immediately, even
        // before the user hits Play. Keeps a history of every AI generation
        // so a closed modal doesn't waste a Claude API call.
        if (player?.addToLibrary && data.playlist?.length) {
          const trimmedNotes = userNotes.trim();
          const activityContext = {
            activityId: selectedId,
            activityName: selectedAct?.name || "Ride",
            activityType: selectedAct?.type,
            activityDate: selectedAct?.startDate,
            distanceMeters: selectedAct?.distance,
            elevationGainMeters: selectedAct?.elevationGain,
            elevation: data.elevation || [],
            totalDurationSec: data.totalDurationSec || 0,
            totalDistanceMeters: data.totalDistanceMeters || 0,
            segments: data.segments || [],
            // Build settings — captured so the player tile can show a
            // pill summary ("how was this playlist made?") and a future
            // "replicate" action could re-seed the builder from these.
            buildSettings: {
              varietyMode: varietyMode || "balanced",
              discoveryMode: discoveryMode || "library",
              presetLabel: matchPresetLabel(trimmedNotes),
              notes: trimmedNotes || null,
            },
          };
          player.addToLibrary(data.playlist, fullPlaylistName, activityContext);
        }
      }
    } catch (e) { setError(e.message); }
    setPreviewing(false);
    setGenProgress(null);
  };

  const handleCreate = async () => {
    if (!preview || preview.playlist.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const trackUris = preview.playlist.map(t => t.uri);
      const res = await fetch("/api/create-ride-playlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: fullPlaylistName, description, trackUris }),
      });
      const data = await res.json();
      if (data.error) setError(data.error);
      else setCreated(data);
    } catch (e) { setError(e.message); }
    setCreating(false);
  };

  // If generation is in flight, confirm before closing — stops accidental
  // mid-generation dismissal that wastes a Claude API call.
  const safeClose = () => {
    if (previewing) {
      const ok = window.confirm(
        "FigJam is generating your playlist. If you leave now, this generation will be lost. Are you sure?"
      );
      if (!ok) return;
    }
    onClose();
  };

  // No backdrop click-to-close on the wrapper div below — on mobile,
  // accidental taps in the side strip while reaching for inputs were
  // dismissing the modal mid-edit. User exits explicitly via the X button
  // (top-right) or Cancel button at the bottom.
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 250, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: 20, overflowY: "auto" }}>
      <div style={{ background: "#0c1016", border: "1px solid rgba(29,185,84,0.25)", borderRadius: 16, width: "100%", maxWidth: 540, padding: 24, marginTop: 30, marginBottom: 30 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontFamily: "'Playfair Display',serif", color: "#1DB954" }}>🚵 Build Ride Playlist</h2>
          <button onClick={safeClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 22 }}>✕</button>
        </div>

        {error && (
          <div style={{ fontSize: 12, color: "#FC4C02", marginBottom: 12, background: "rgba(252,76,2,0.1)", padding: "8px 12px", borderRadius: 8 }}>{error}</div>
        )}

        {created ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1DB954", marginBottom: 4 }}>Playlist Created!</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>{created.name} · {created.trackCount} tracks</div>
            <a href={created.url} target="_blank" rel="noreferrer" style={{ display: "inline-block", background: "#1DB954", color: "#fff", textDecoration: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
              Open in Spotify
            </a>
          </div>
        ) : !preview ? (
          <>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 10 }}>Pick a past ride to model the playlist after:</div>
            {loadingActivities ? (
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", padding: 20, textAlign: "center" }}>Loading activities…</div>
            ) : activities.length === 0 ? (
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", padding: 20, textAlign: "center" }}>No rides found in your Strava history</div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 16 }}>
                {activities.map(act => (
                  <div
                    key={act.id}
                    onClick={() => handleSelect(act)}
                    style={{
                      padding: "10px 12px", marginBottom: 6, borderRadius: 8,
                      background: selectedId === act.id ? "rgba(29,185,84,0.15)" : "rgba(255,255,255,0.03)",
                      border: `1px solid ${selectedId === act.id ? "#1DB954" : "rgba(255,255,255,0.07)"}`,
                      cursor: "pointer", transition: "background 0.15s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{act.name}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontFamily: "monospace", marginTop: 2 }}>
                          {act.type} · {fmtDate(act.startDate)} · {fmtMi(act.distance)} · {fmtTime(act.movingTime)} · {Math.round(act.elevationGain * 3.28084)} ft
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedId && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>
                    Name this playlist
                  </label>
                  <input
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    placeholder="e.g., Morning Crusher"
                    maxLength={80}
                    style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                  />
                  <div style={{
                    fontSize: 10, color: "rgba(255,255,255,0.4)",
                    fontFamily: "monospace", marginTop: 4, paddingLeft: 2,
                  }}>
                    {trimmedCustom ? (
                      <>Saves as: <span style={{ color: "#1DB954" }}>{fullPlaylistName}</span></>
                    ) : (
                      <>Date (<span style={{ color: "rgba(255,255,255,0.55)" }}>{todayStr}</span>) and "Figjam Playlist" will be appended automatically</>
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>Description</label>
                  <input value={description} onChange={e => setDescription(e.target.value)} style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    fontSize: 11, color: "rgba(255,255,255,0.5)",
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    marginBottom: 4,
                  }}>
                    <span>
                      <span style={{ color: "#E8D44D" }}>🤖</span> Tell FigJam what you want
                      <span style={{ color: "rgba(255,255,255,0.3)", marginLeft: 6, fontWeight: 400 }}>(optional)</span>
                    </span>
                    <span style={{ color: "rgba(255,255,255,0.35)", fontFamily: "monospace", fontSize: 10 }}>
                      {userNotes.length}/800
                    </span>
                  </label>

                  {/* Quick-fill preset pills — list hoisted to module scope
                      so both this builder and the player tile (build-summary
                      pills) can match by prompt string for a friendly label. */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {FIGJAM_BUILDER_PRESETS.map((preset, i) => (
                      <button
                        key={i}
                        onClick={() => setUserNotes(preset.prompt)}
                        type="button"
                        title={`Click to fill: ${preset.prompt}`}
                        style={{
                          background: userNotes === preset.prompt ? "rgba(232,212,77,0.2)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${userNotes === preset.prompt ? "rgba(232,212,77,0.5)" : "rgba(255,255,255,0.1)"}`,
                          borderRadius: 14, padding: "5px 10px",
                          color: userNotes === preset.prompt ? "#E8D44D" : "rgba(255,255,255,0.75)",
                          fontSize: 11, fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "'DM Sans',sans-serif",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>

                  <textarea
                    value={userNotes}
                    onChange={e => setUserNotes(e.target.value.slice(0, 800))}
                    placeholder={`e.g., "Metal like Tool and Metallica on climbs. Mellow jam-band music like Phish or Grateful Dead on descents. No acoustic stuff."`}
                    rows={3}
                    style={{
                      width: "100%",
                      background: "rgba(232,212,77,0.04)",
                      border: "1px solid rgba(232,212,77,0.2)",
                      borderRadius: 8, padding: "9px 12px",
                      color: "#fff", fontSize: 13,
                      outline: "none", boxSizing: "border-box",
                      fontFamily: "'DM Sans',sans-serif",
                      resize: "vertical",
                      minHeight: 60,
                    }}
                  />
                  <div style={{
                    fontSize: 10, color: "rgba(255,255,255,0.4)",
                    fontFamily: "monospace", marginTop: 4, paddingLeft: 2,
                  }}>
                    Name specific artists, genres, or moods per terrain type. FigJam picks the closest matches from your library.
                  </div>
                </div>

                {/* Discovery mode toggle — pick from library only or explore */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    fontSize: 11, color: "rgba(255,255,255,0.5)",
                    display: "block", marginBottom: 6,
                  }}>
                    Music source
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => setDiscoveryMode("library")}
                      style={{
                        flex: 1, padding: "10px",
                        background: discoveryMode === "library" ? "rgba(29,185,84,0.2)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${discoveryMode === "library" ? "#1DB954" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: 8,
                        color: discoveryMode === "library" ? "#1DB954" : "rgba(255,255,255,0.7)",
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                        fontFamily: "'DM Sans',sans-serif",
                        textAlign: "left",
                      }}
                      title="Use only songs from your Spotify Liked + Top tracks. Faster generation."
                    >
                      <div>📚 My library only</div>
                      <div style={{ fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                        Liked + top tracks · faster
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscoveryMode("explore")}
                      style={{
                        flex: 1, padding: "10px",
                        background: discoveryMode === "explore" ? "rgba(232,212,77,0.2)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${discoveryMode === "explore" ? "#E8D44D" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: 8,
                        color: discoveryMode === "explore" ? "#E8D44D" : "rgba(255,255,255,0.7)",
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                        fontFamily: "'DM Sans',sans-serif",
                        textAlign: "left",
                      }}
                      title="Pull deep cuts from artists in your library and artists you follow on Spotify, even if you haven't liked those specific songs."
                    >
                      <div>✨ Explore new music</div>
                      <div style={{ fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                        + deep cuts from your faves · slower
                      </div>
                    </button>
                  </div>
                </div>

                {/* ── Variety mode radio (must pick one before generating) ── */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    fontSize: 11, color: "rgba(255,255,255,0.5)",
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    marginBottom: 6,
                  }}>
                    <span>
                      <span style={{ color: "#1DB954" }}>🎚️</span> Variety mode
                      <span style={{ color: "#FC4C02", marginLeft: 6, fontWeight: 700 }}>(required)</span>
                    </span>
                    <span style={{ color: "rgba(255,255,255,0.35)", fontFamily: "monospace", fontSize: 10 }}>
                      🔴 = recommended for your prompt
                    </span>
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[
                      { key: "discover",  emoji: "✨", label: "Wide variety",  sub: "Many different artists", caps: "2/1/1" },
                      { key: "balanced",  emoji: "⚖️", label: "Balanced",      sub: "Mix of artists",         caps: "3/2/1" },
                      { key: "favorites", emoji: "🎯", label: "Favorites",     sub: "More from named artists", caps: "4/3/1" },
                    ].map(opt => {
                      const isSelected = varietyMode === opt.key;
                      const isRecommended = recommendedMode === opt.key;
                      // Recommended: red border when not selected; selected always green
                      const borderColor = isSelected ? "#1DB954" : isRecommended ? "#FC4C02" : "rgba(255,255,255,0.12)";
                      const bg = isSelected ? "rgba(29,185,84,0.2)" : isRecommended ? "rgba(252,76,2,0.08)" : "rgba(255,255,255,0.04)";
                      const textColor = isSelected ? "#1DB954" : isRecommended ? "#FC4C02" : "rgba(255,255,255,0.85)";
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setVarietyMode(opt.key)}
                          title={`${opt.label} — caps: featured / requested / other = ${opt.caps}${isRecommended ? "  (recommended for this prompt)" : ""}`}
                          style={{
                            flex: 1, padding: "8px 6px",
                            background: bg,
                            border: `1.5px solid ${borderColor}`,
                            borderRadius: 8,
                            color: textColor,
                            cursor: "pointer",
                            fontFamily: "'DM Sans',sans-serif",
                            transition: "all 0.15s ease",
                            boxShadow: isRecommended && !isSelected ? "0 0 8px rgba(252,76,2,0.3)" : "none",
                          }}
                        >
                          <div style={{ fontSize: 18, lineHeight: 1, marginBottom: 2 }}>{opt.emoji}</div>
                          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 1 }}>{opt.label}</div>
                          <div style={{ fontSize: 9, opacity: 0.75, fontFamily: "monospace" }}>{opt.sub}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Library-full check — count non-favorited saved entries
                    against FIGJAM_LIBRARY_CAP. When at cap, block generation
                    and prompt the user to delete or favorite a playlist
                    first. Favorites don't count toward the cap. */}
                {(() => {
                  const nonFavCount = (player?.library || []).filter(e => !e.favorited).length;
                  const atCap = nonFavCount >= FIGJAM_LIBRARY_CAP;
                  return (
                    <>
                      {atCap && (
                        <div style={{
                          marginBottom: 10, padding: "12px 14px",
                          background: "rgba(252,76,2,0.08)",
                          border: "1px solid rgba(252,76,2,0.35)",
                          borderRadius: 10,
                          fontSize: 12, color: "rgba(255,255,255,0.85)",
                          fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5,
                        }}>
                          <div style={{ fontWeight: 700, color: "#FC4C02", marginBottom: 4 }}>
                            🗂️ Playlist library is full ({nonFavCount} / {FIGJAM_LIBRARY_CAP})
                          </div>
                          <div style={{ marginBottom: 10, color: "rgba(255,255,255,0.65)" }}>
                            Delete one to make room, or ♥ favorite the ones you want to keep (favorites don't count against the limit).
                          </div>
                          <button
                            type="button"
                            onClick={onOpenLibrary}
                            style={{
                              background: "#FC4C02", border: "none", borderRadius: 8,
                              padding: "8px 14px", color: "#fff", fontWeight: 700,
                              fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                            }}
                          >
                            Open Playlist Library
                          </button>
                        </div>
                      )}
                      <button
                        onClick={handlePreview}
                        disabled={previewing || !trimmedCustom || !varietyMode || atCap}
                        title={
                          atCap
                            ? `You have ${FIGJAM_LIBRARY_CAP} saved playlists already. Delete or favorite one to make room.`
                            : !trimmedCustom
                              ? "Enter a name for your playlist first"
                              : !varietyMode
                                ? "Pick a variety mode above"
                                : undefined
                        }
                        style={{ width: "100%", padding: "12px", background: previewing || !trimmedCustom || !varietyMode || atCap ? "rgba(29,185,84,0.4)" : "#1DB954", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: previewing ? "wait" : (!trimmedCustom || !varietyMode || atCap) ? "not-allowed" : "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
                      >
                        {previewing && (
                          <span
                            aria-label="loading"
                            style={{
                              display: "inline-block",
                              width: 16, height: 16,
                              border: "2.5px solid rgba(255,255,255,0.3)",
                              borderTopColor: "#fff",
                              borderRadius: "50%",
                              animation: "figjamSpin 0.7s linear infinite",
                            }}
                          />
                        )}
                        <span>
                          {previewing
                            ? (discoveryMode === "explore" || matchPresetLabel(userNotes.trim())
                                ? "🌿 Deep search — finding the perfect tracks…"
                                : "FigJam is analyzing terrain & curating songs…")
                            : atCap
                              ? "🗂️ Library full — delete one to continue"
                              : !varietyMode
                                ? "🎚️ Pick a variety mode above"
                                : "🤖 Generate AI Playlist"}
                        </span>
                      </button>
                    </>
                  );
                })()}
                {previewing && (() => {
                  const isDeepSearch = discoveryMode === "explore" || !!matchPresetLabel(userNotes.trim());
                  // Real progress is available once the backend has reported at
                  // least one segment update. Before that we just show the
                  // "starting up" state — the swarm needs to fetch the library
                  // + segment the ride before workers can fire.
                  const hasProgress = genProgress && genProgress.mode === "swarm" && genProgress.total > 0;
                  const pct = hasProgress ? Math.round((genProgress.completed / genProgress.total) * 100) : 0;
                  const accent = isDeepSearch ? "#E8D44D" : "#1DB954";
                  return (
                    <div style={{
                      marginTop: 12, padding: "10px 14px",
                      background: isDeepSearch ? "rgba(232,212,77,0.10)" : "rgba(29,185,84,0.08)",
                      border: `1px solid ${isDeepSearch ? "rgba(232,212,77,0.30)" : "rgba(29,185,84,0.25)"}`,
                      borderRadius: 10,
                      fontSize: 11, color: "rgba(255,255,255,0.7)",
                      fontFamily: "monospace", textAlign: "center", lineHeight: 1.6,
                    }}>
                      {hasProgress ? (
                        <>
                          <div style={{ marginBottom: 8, color: accent, fontWeight: 700, letterSpacing: 0.5 }}>
                            🌿 DEEP SEARCH · {genProgress.completed} of {genProgress.total} SEGMENTS DONE
                          </div>
                          <div style={{
                            width: "100%", height: 6,
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: 3, overflow: "hidden",
                            marginBottom: 6,
                          }}>
                            <div style={{
                              width: `${pct}%`, height: "100%",
                              background: accent,
                              transition: "width 0.4s ease-out",
                            }} />
                          </div>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
                            <span style={{ color: "#FC8B02" }}>Don't close this window</span>.
                          </div>
                        </>
                      ) : isDeepSearch ? (
                        <>
                          🌿 Deep search starting — multiple AI workers will run in parallel (30–60 seconds).{" "}
                          <span style={{ color: "#FC8B02" }}>Don't close this window</span>.
                        </>
                      ) : (
                        <>
                          Generation usually takes 10–60 seconds.{" "}
                          <span style={{ color: "#FC8B02" }}>Don't close this window</span> — your playlist will be lost.
                        </>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </>
        ) : (
          // Preview view
          <>
            <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 6, fontFamily: "monospace" }}>TERRAIN BREAKDOWN · {preview.segments.length} segments</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {preview.segments.map((s, i) => {
                  const colors = { "steep-climb": "#FC4C02", "climb": "#FC8B02", "flat": "#1DB954", "descent": "#4169E1", "steep-descent": "#7B4DFF" };
                  const c = colors[s.kind] || "#888";
                  const minutes = Math.round(s.durationSec / 60);
                  if (minutes < 1) return null;
                  return (
                    <span key={i} style={{ background: `${c}25`, border: `1px solid ${c}60`, color: c, fontSize: 10, padding: "3px 8px", borderRadius: 12, fontWeight: 600 }}>
                      {s.kind.replace("-", " ")} · {minutes}m
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>
              {preview.playlist.length} tracks · AI-curated by FigJam
            </div>
            {preview.exclusion && preview.exclusion.actuallyExcluded > 0 && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "rgba(29,185,84,0.1)",
                border: "1px solid rgba(29,185,84,0.3)",
                borderRadius: 8, padding: "4px 10px", marginBottom: 10,
                fontSize: 10, color: "#1DB954", fontFamily: "monospace",
              }} title={`Your saved playlists contained ${preview.exclusion.requestedExcludes} unique tracks. Those were excluded from this generation — it had ${preview.exclusion.candidatePool} candidate songs to choose from (from your expanded Liked + Top library).`}>
                ✨ FRESH · Excluded {preview.exclusion.actuallyExcluded} track{preview.exclusion.actuallyExcluded !== 1 ? "s" : ""} from recent playlists
              </div>
            )}
            {preview.exclusion && preview.exclusion.actuallyExcluded === 0 && preview.exclusion.requestedExcludes > 0 && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "rgba(252,76,2,0.08)",
                border: "1px solid rgba(252,76,2,0.25)",
                borderRadius: 8, padding: "4px 10px", marginBottom: 10,
                fontSize: 10, color: "#FC8B02", fontFamily: "monospace",
              }} title="Your library is too small to filter out recent tracks — like more songs on Spotify for better variety.">
                ⚠ Pool too small — recent tracks may repeat
              </div>
            )}
            <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 14 }}>
              {preview.playlist.map((t, i) => {
                const intensityColors = { peak: "#FC4C02", high: "#FC8B02", medium: "#1DB954", low: "#4169E1" };
                const intensityColor = intensityColors[t.intensityRating] || "#1DB954";
                return (
                  <div key={i} style={{ padding: "8px 10px", marginBottom: 4, background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {t.albumArt && <img src={t.albumArt} alt="" style={{ width: 32, height: 32, borderRadius: 4, flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.artists}</div>
                      </div>
                      <div style={{ flexShrink: 0, fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: "monospace", textAlign: "right" }}>
                        <div style={{ color: intensityColor, fontWeight: 700 }}>{t.intensityRating?.toUpperCase()}</div>
                        <div style={{ color: "#1DB954" }}>{t.segmentLabel}</div>
                      </div>
                    </div>
                    {t.reasoning && (
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4, paddingLeft: 40, fontStyle: "italic" }}>
                        💭 {t.reasoning}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Play in Figjam — primary action using Spotify Web Playback SDK */}
            {player && (
              <button
                onClick={async () => {
                  const trimmedNotes = userNotes.trim();
                  const activityContext = {
                    activityId: selectedId,
                    activityName: selectedAct?.name || "Ride",
                    activityType: selectedAct?.type,
                    activityDate: selectedAct?.startDate,
                    distanceMeters: selectedAct?.distance,
                    elevationGainMeters: selectedAct?.elevationGain,
                    elevation: preview.elevation || [],
                    totalDurationSec: preview.totalDurationSec || 0,
                    totalDistanceMeters: preview.totalDistanceMeters || 0,
                    segments: preview.segments || [],
                    buildSettings: {
                      varietyMode: varietyMode || "balanced",
                      discoveryMode: discoveryMode || "library",
                      presetLabel: matchPresetLabel(trimmedNotes),
                      notes: trimmedNotes || null,
                    },
                  };
                  await player.playQueue(preview.playlist, fullPlaylistName, activityContext);
                  onClose();
                }}
                disabled={player.status === "connecting"}
                style={{ width: "100%", padding: "14px", marginBottom: 8, background: player.status === "connecting" ? "rgba(65,105,225,0.4)" : "#4169E1", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 15, cursor: player.status === "connecting" ? "wait" : "pointer", fontFamily: "'DM Sans',sans-serif" }}
              >
                {player.status === "connecting" ? "⏳ Connecting player…" : "▶ Play in Figjam"}
              </button>
            )}
            {player?.error && (
              <div style={{ fontSize: 11, color: "#FC4C02", marginBottom: 8, background: "rgba(252,76,2,0.08)", padding: "6px 10px", borderRadius: 6 }}>
                {player.error}
              </div>
            )}

            {/* Secondary: back + create-in-Spotify (still blocked by dev mode 403 but kept as fallback) */}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setPreview(null)} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "rgba(255,255,255,0.7)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                ← Back
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{ flex: 2, padding: "10px", background: creating ? "rgba(29,185,84,0.4)" : "rgba(29,185,84,0.15)", border: "1px solid #1DB954", borderRadius: 10, color: "#1DB954", fontWeight: 600, fontSize: 12, cursor: creating ? "wait" : "pointer", fontFamily: "'DM Sans',sans-serif" }}
              >
                {creating ? "Creating…" : "Also save to Spotify"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConnectionsPanel({ status, onClose, onDisconnect, supabaseSession }) {
  // When signed in via Supabase, Strava goes through the v2 multi-user
  // flow that ties tokens to the user's account in Postgres.
  const handleConnectStrava = async () => {
    if (supabaseSession?.access_token) {
      try {
        const res = await fetch("/api/v2/strava/start", {
          headers: { Authorization: `Bearer ${supabaseSession.access_token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { url } = await res.json();
        window.location.href = url;
        return;
      } catch (e) {
        console.warn("[connect] v2 strava start failed, falling back:", e.message);
      }
    }
    // Legacy fallback (single-user, /auth/strava → tokenStore)
    window.location.href = "http://127.0.0.1:3001/auth/strava";
  };
  const handleConnectSpotify = () => {
    // Spotify still uses the legacy /auth/spotify path here. The Supabase
    // "Sign in (β)" button in the header is the multi-user equivalent.
    window.location.href = "http://127.0.0.1:3001/auth/spotify";
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0c1016", borderLeft: "1px solid rgba(255,255,255,0.07)", width: "100%", maxWidth: 380, padding: 24, overflowY: "auto", height: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontFamily: "'Playfair Display',serif" }}>Connections</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        {Object.entries(SVC).map(([key, svc]) => {
          const s = status[key] || {};
          return (
            <div key={key} style={{ marginBottom: 20, background: svc.bg, border: `1px solid ${svc.color}22`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: s.connected ? "#34D399" : "rgba(255,255,255,0.2)",
                  boxShadow: s.connected ? "0 0 8px #34D399" : "none",
                }} />
                <span style={{ fontSize: 15, fontWeight: 700, color: svc.color, fontFamily: "'Playfair Display',serif" }}>{svc.label}</span>
              </div>

              {s.connected ? (
                <>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 10, fontFamily: "monospace" }}>
                    Connected{s.updatedAt ? ` · last synced ${ago(s.updatedAt)}` : ""}
                  </div>
                  {s.error && (
                    <div style={{ fontSize: 11, color: "#FC4C02", marginBottom: 10, background: "rgba(252,76,2,0.08)", padding: "6px 10px", borderRadius: 6 }}>
                      {s.error}
                    </div>
                  )}
                  <button
                    onClick={() => onDisconnect(key)}
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "6px 14px", color: "rgba(255,255,255,0.5)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 12, lineHeight: 1.6 }}>
                    Click below to authorize with {svc.label}. You'll be redirected to log in and grant access, then sent back here automatically.
                  </div>
                  <button
                    onClick={() => {
                      if (key === "strava") return handleConnectStrava();
                      if (key === "spotify") return handleConnectSpotify();
                      window.location.href = `http://127.0.0.1:3001/auth/${key}`;
                    }}
                    style={{ background: svc.color, border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", width: "100%" }}
                  >
                    Connect {svc.label}{key === "strava" && supabaseSession ? " (multi-user)" : ""}
                  </button>
                </>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: 16, fontSize: 10, color: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.02)", padding: "10px 12px", borderRadius: 8, lineHeight: 1.7 }}>
          Tokens are stored on the server and refresh automatically. Your credentials never touch the browser.
        </div>
      </div>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [items,        setItems]       = useState([]);
  const [lSvc,         setLSvc]        = useState({});
  const [errors,       setErrors]      = useState({});
  const [expanded,     setExpanded]    = useState(null);
  const [filter,       setFilter]      = useState("all");
  const [spotifyTypes, setSpotifyTypes]= useState(new Set(["Now Playing", "Played"]));
  const [search,       setSearch]      = useState("");
  const [showPanel,    setShowPanel]   = useState(false);
  const [showRideBuilder, setShowRideBuilder] = useState(false);
  const [showPlayerTest, setShowPlayerTest] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  // When the user taps Build Ride Playlist while at the library cap we
  // redirect them into the Library modal. Flag is set at that moment so we
  // know to auto-open the builder once they've freed up a slot.
  const [pendingBuilderAfterDelete, setPendingBuilderAfterDelete] = useState(false);
  const figjamPlayer = useFigjamPlayer();
  // Auto-flip from Library back into the Builder once the user has cleared
  // enough entries to drop below the cap. Only fires when we entered the
  // library via the cap-gate path (pendingBuilderAfterDelete is true).
  useEffect(() => {
    if (!pendingBuilderAfterDelete || !showLibrary) return;
    const nonFavCount = (figjamPlayer.library || []).filter(e => !e.favorited).length;
    if (nonFavCount < FIGJAM_LIBRARY_CAP) {
      setShowLibrary(false);
      setShowRideBuilder(true);
      setPendingBuilderAfterDelete(false);
    }
  }, [pendingBuilderAfterDelete, showLibrary, figjamPlayer.library]);
  const [booted,       setBooted]      = useState(false);
  const [status,       setStatus]      = useState({});

  // ─── Supabase session (Phase A + B of backend migration) ─────────────────
  // Tracks auth state AND syncs Spotify tokens to Postgres on sign-in.
  // session.provider_token = Spotify access token (from OAuth)
  // session.provider_refresh_token = Spotify refresh token
  // We POST these to /api/spotify/sync-tokens so the backend can use them
  // to make Spotify API calls on behalf of the user, replacing tokens.json.
  const [supabaseSession, setSupabaseSession] = useState(null);
  useEffect(() => {
    let alreadySynced = false;
    const syncSpotifyTokensIfNeeded = async (session) => {
      if (alreadySynced || !session?.provider_token) return;
      try {
        await fetch("/api/spotify/sync-tokens", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            access_token: session.provider_token,
            refresh_token: session.provider_refresh_token || "",
            expires_in: 3600, // Spotify default; backend computes expires_at
          }),
        });
        alreadySynced = true;
        console.log("[figjam] Spotify tokens synced to Postgres");
      } catch (e) {
        console.warn("[figjam] sync-tokens failed:", e.message);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
      syncSpotifyTokensIfNeeded(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSupabaseSession(session);
        if (event === "SIGNED_IN") syncSpotifyTokensIfNeeded(session);
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  // ── v2 Strava connection status (per-user, Postgres-backed) ───────────────
  // Powers the "Connect Strava" prompt shown after Spotify sign-in until the
  // user finishes the Strava OAuth dance. null = unknown, true/false = known.
  const [stravaV2Connected, setStravaV2Connected] = useState(null);
  const refreshStravaV2Status = useCallback(async () => {
    if (!supabaseSession?.access_token) { setStravaV2Connected(null); return; }
    try {
      const r = await fetch("/api/v2/strava/status", {
        headers: { Authorization: `Bearer ${supabaseSession.access_token}` },
      });
      if (!r.ok) { setStravaV2Connected(false); return; }
      const data = await r.json();
      setStravaV2Connected(!!data.connected);
    } catch { setStravaV2Connected(false); }
  }, [supabaseSession?.access_token]);
  useEffect(() => { refreshStravaV2Status(); }, [refreshStravaV2Status]);

  // Detect ?connected=strava query param (set by backend after OAuth round-
  // trip) and immediately refresh status — closes the loop so the prompt
  // disappears as soon as the user lands back from Strava.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "strava") {
      refreshStravaV2Status();
      // Strip the query param so reloads don't re-trigger
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [refreshStravaV2Status]);

  // Kick off the v2 Strava OAuth flow. Mirrors handleConnectStrava in the
  // ConnectionsPanel but lives at the top level so the welcome / prompt
  // cards can call it directly.
  const startStravaConnect = useCallback(async () => {
    if (!supabaseSession?.access_token) return;
    try {
      const r = await fetch("/api/v2/strava/start", {
        headers: { Authorization: `Bearer ${supabaseSession.access_token}` },
      });
      const { url } = await r.json();
      if (url) window.location.href = url;
    } catch (e) {
      console.warn("[figjam] strava connect failed", e.message);
    }
  }, [supabaseSession?.access_token]);

  const [toast,        setToast]       = useState(null);
  const [expandedPlaylist, setExpandedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState({});

  // ── Toggle a Spotify sub-type filter ────────────────────────────────────────
  const toggleType = useCallback((key) => {
    setSpotifyTypes(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // ── Fetch from backend ──────────────────────────────────────────────────────
  const fetchTimeline = useCallback(async () => {
    setLSvc({ spotify: true, strava: true });
    try {
      const [tlRes, stRes] = await Promise.all([
        fetch("/api/timeline"),
        fetch("/api/status"),
      ]);
      const tl = await tlRes.json();
      const st = await stRes.json();
      // Dedupe Spotify "Played" items: the local now-playing detector and the
      // Recently Played API both record the same track but with different
      // timestamps (synthesized start vs. Spotify's end-time), so their ids
      // differ and cache-by-id doesn't catch them. Items are sorted desc by
      // timestamp; walk in order and drop any "Played" entry whose spotifyUri
      // already appeared within the last 3 minutes — keeps the most recent
      // (usually the API's authoritative entry with richer context).
      const rawItems = tl.items || [];
      const lastSeenByUri = new Map(); // spotifyUri → timestamp (ms)
      const dedupedItems = rawItems.filter(item => {
        if (item.service !== "spotify" || item.type !== "Played") return true;
        const uri = item.extra?.spotifyUri;
        if (!uri) return true;
        const ts = new Date(item.timestamp).getTime();
        const prevTs = lastSeenByUri.get(uri);
        if (prevTs != null && Math.abs(ts - prevTs) <= 3 * 60 * 1000) return false;
        lastSeenByUri.set(uri, ts);
        return true;
      });
      setItems(dedupedItems);
      setErrors(tl.errors || {});
      setStatus(st);
    } catch (e) {
      setErrors({ _network: e.message });
    }
    setLSvc({});
  }, []);

  // ── Boot: check URL params from OAuth callback, then fetch ────────────────
  useEffect(() => {
    if (booted) return;
    setBooted(true);

    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const error = params.get("error");

    if (connected) {
      const label = SVC[connected]?.label || connected;
      setToast({ msg: `${label} connected!`, color: "#34D399" });
      window.history.replaceState({}, "", "/");
      setTimeout(fetchTimeline, 2000);
    } else if (error) {
      // Log everything so we can debug (URL gets cleared right after)
      const errorDescription = params.get("error_description") || "";
      const errorCode = params.get("error_code") || "";
      // eslint-disable-next-line no-console
      console.error("[oauth] error:", error, "| code:", errorCode, "| desc:", errorDescription, "| full url:", window.location.href);
      const msg = error.includes("denied")
        ? `Auth denied: ${errorDescription || error}`
        : `Auth failed: ${errorDescription || error}`;
      setToast({ msg, color: "#FC4C02" });
      window.history.replaceState({}, "", "/");
      fetchTimeline();
    } else {
      fetchTimeline();
    }
  }, [booted, fetchTimeline]);

  // ── Auto-refresh every 10s to catch song changes ────────────────────────────
  // Only auto-poll when the legacy single-user tokenStore is connected — for
  // multi-user signups the timeline endpoint returns empty (Phase B/D in
  // progress), so polling just causes layout jank with no data to show.
  useEffect(() => {
    const legacyConnected = status.spotify?.connected || status.strava?.connected;
    if (!legacyConnected) return; // skip polling for multi-user / signed-out
    const interval = setInterval(fetchTimeline, 10000);
    return () => clearInterval(interval);
  }, [fetchTimeline, status.spotify?.connected, status.strava?.connected]);

  // ── Auto-dismiss toast ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Disconnect a service ────────────────────────────────────────────────────
  // When Supabase-signed-in, also calls the v2 disconnect endpoint to clear
  // the Postgres token row. Without this, "disconnect Strava" only clears
  // the legacy filesystem store — the per-user row stays, so re-prompting
  // doesn't work for testing the connect flow.
  const disconnect = useCallback(async (service) => {
    await fetch("/api/disconnect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ service }),
    });
    if (supabaseSession?.access_token && service === "strava") {
      try {
        await fetch("/api/v2/strava/disconnect", {
          method: "POST",
          headers: { Authorization: `Bearer ${supabaseSession.access_token}` },
        });
      } catch (e) {
        console.warn("[figjam] v2 strava disconnect failed:", e.message);
      }
      refreshStravaV2Status();
    }
    fetchTimeline();
  }, [fetchTimeline, supabaseSession?.access_token, refreshStravaV2Status]);

  // ── Filtered view ─────────────────────────────────────────────────────────────
  // When Figjam player is active, hide the Spotify "Now Playing" tile —
  // our FigjamPlayerTile replaces it (and Spotify's API would show the same
  // track since our SDK plays through their system).
  const figjamActive = figjamPlayer.queue.length > 0 || figjamPlayer.track;

  const filtered = items.filter(item => {
    const fOk = filter === "all" || item.service === filter;
    const typeOk = filter !== "spotify" || spotifyTypes.size === 0 || spotifyTypes.has(item.type);
    const sOk = !search || item.title.toLowerCase().includes(search.toLowerCase()) || item.subtitle.toLowerCase().includes(search.toLowerCase());
    // Hide standalone Playlist cards from the "all" view — playlists show as context labels between tracks
    if (filter === "all" && item.type === "Playlist") return false;
    // Hide Spotify Now Playing when Figjam player is active (our tile takes its place)
    if (figjamActive && item.service === "spotify" && item.type === "Now Playing") return false;
    return fOk && typeOk && sOk;
  });

  // ── Group Spotify tracks under Strava activities when they overlap in time ──
  const grouped = (() => {
    if (filter === "strava" || filter === "spotify") return filtered.map(item => ({ kind: "single", item }));

    const stravaActivities = filtered.filter(i => i.service === "strava" && i.extra?.movingTimeSec);
    if (stravaActivities.length === 0) return filtered.map(item => ({ kind: "single", item }));

    // Build time windows for each Strava activity
    const windows = stravaActivities.map(act => {
      const start = new Date(act.timestamp).getTime();
      const end = start + (act.extra.movingTimeSec * 1000);
      return { activity: act, start, end };
    });

    // Find which Spotify tracks fall within a Strava activity window
    const claimedIds = new Set();
    const activitySongs = new Map(); // activityId → [songs]
    filtered.forEach(item => {
      if (item.service !== "spotify" || (item.type !== "Played" && item.type !== "Now Playing")) return;
      const t = new Date(item.timestamp).getTime();
      for (const w of windows) {
        if (t >= w.start && t <= w.end) {
          if (!activitySongs.has(w.activity.id)) activitySongs.set(w.activity.id, []);
          activitySongs.get(w.activity.id).push(item);
          claimedIds.add(item.id);
          break;
        }
      }
    });

    // Build grouped list
    return filtered.filter(i => !claimedIds.has(i.id)).map(item => {
      if (item.service === "strava" && activitySongs.has(item.id)) {
        return { kind: "strava-with-songs", activity: item, songs: activitySongs.get(item.id) };
      }
      return { kind: "single", item };
    });
  })();

  const anyLoading = Object.values(lSvc).some(Boolean);
  // Treat the user as connected when they have a Supabase session — the
  // legacy /api/status endpoint returns "not connected" on Render (filesystem
  // tokenStore is ephemeral and empty), so the multi-user path needs this
  // bypass to hide the empty-state Connect CTAs after sign-in.
  const anyConnected = !!supabaseSession || status.spotify?.connected || status.strava?.connected;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#070b0f", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 60 }}>
      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: rgba(255,255,255,0.3); }
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes figjamSpin { to { transform: rotate(360deg); } }
        @keyframes toastIn { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:none; } }
        @keyframes figjamHeaderPulse {
          0%, 100% { opacity: 0.55; text-shadow: 0 0 0 rgba(255,255,255,0); }
          50%      { opacity: 1;    text-shadow: 0 0 14px rgba(255,255,255,0.35); }
        }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
      `}</style>

      {/* Toast notification */}
      {toast && (
        <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 500, background: "#0f1419", border: `1px solid ${toast.color}40`, borderRadius: 10, padding: "10px 20px", animation: "toastIn 0.25s ease", boxShadow: `0 4px 20px ${toast.color}30` }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: toast.color }}>{toast.msg}</span>
        </div>
      )}

      {/* Ambient blobs */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
        {[{ c: "#1DB954", x: "12%", y: "22%" },
          { c: "#FC4C02", x: "82%", y: "72%" }]
          .map((b, i) => (
            <div key={i} style={{ position: "absolute", left: b.x, top: b.y, width: 420, height: 420, borderRadius: "50%", background: b.c, opacity: 0.035, filter: "blur(110px)", transform: "translate(-50%,-50%)" }} />
          ))}
      </div>

      {showPanel && <ConnectionsPanel status={status} onClose={() => setShowPanel(false)} onDisconnect={disconnect} supabaseSession={supabaseSession} />}
      {showRideBuilder && <RidePlaylistBuilder onClose={() => setShowRideBuilder(false)} player={figjamPlayer} supabaseSession={supabaseSession} onOpenLibrary={() => { setShowRideBuilder(false); setShowLibrary(true); }} />}
      {showPlayerTest && <SpotifyPlayerTest onClose={() => setShowPlayerTest(false)} />}
      {showLibrary && <PlaylistLibrary player={figjamPlayer} onClose={() => { setShowLibrary(false); setPendingBuilderAfterDelete(false); }} />}

      {/* ── Header ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(7,11,15,0.92)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "16px 18px 12px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: 12, gap: 6 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {/* Supabase auth — Phase A test button. Will replace the
                  existing /auth/spotify connect flow once verified. */}
              {!supabaseSession ? (
                <button
                  onClick={() => signInWithSpotify()}
                  title="Sign in with Spotify (via Supabase) — beta"
                  style={{
                    background: "rgba(29,185,84,0.18)",
                    border: "1px solid #1DB954",
                    borderRadius: 8, padding: "6px 12px",
                    color: "#1DB954",
                    fontSize: 11, fontWeight: 700,
                    cursor: "pointer", lineHeight: 1,
                    fontFamily: "'DM Sans',sans-serif",
                    letterSpacing: "0.05em",
                  }}
                >
                  Sign in (β)
                </button>
              ) : (
                <button
                  onClick={async () => { await supabaseSignOut(); }}
                  title={`Signed in as ${supabaseSession.user?.email || supabaseSession.user?.user_metadata?.name || "user"} — click to sign out`}
                  style={{
                    background: "rgba(29,185,84,0.25)",
                    border: "1px solid #1DB954",
                    borderRadius: 8, padding: "6px 10px",
                    color: "#1DB954",
                    fontSize: 11, fontWeight: 700,
                    cursor: "pointer", lineHeight: 1,
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  ✓ {supabaseSession.user?.user_metadata?.name?.split(" ")[0] || "Signed in"}
                </button>
              )}
              <button onClick={() => { fetch("/api/refresh", { method: "POST" }); setTimeout(fetchTimeline, 2000); }} title="Refresh" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 8, padding: "6px 10px", color: "rgba(255,255,255,0.55)", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>
                <span style={{ display: "inline-block", animation: anyLoading ? "spin 1s linear infinite" : "none" }}>↻</span>
              </button>
              <button
                onClick={() => {
                  // Gate the builder behind the library cap: if the user is
                  // already at the max non-favorited count, send them straight
                  // to the library with a toast explaining why. Favorites
                  // don't count against the cap. Keeps the friction visible
                  // up front instead of letting them fill out the builder
                  // form only to hit the wall on Generate.
                  const nonFavCount = (figjamPlayer.library || []).filter(e => !e.favorited).length;
                  if (nonFavCount >= FIGJAM_LIBRARY_CAP) {
                    setToast({
                      msg: `Library full (${nonFavCount}/${FIGJAM_LIBRARY_CAP}) — delete or ♥ one to make room.`,
                      color: "#FC4C02",
                    });
                    setPendingBuilderAfterDelete(true);
                    setShowLibrary(true);
                  } else {
                    setShowRideBuilder(true);
                  }
                }}
                title="Build Ride Playlist"
                style={{ background: "rgba(29,185,84,0.12)", border: "1px solid rgba(29,185,84,0.3)", borderRadius: 8, padding: "6px 10px", color: "#1DB954", cursor: "pointer", fontSize: 15, lineHeight: 1 }}
              >
                🚵
              </button>
              <button onClick={() => setShowLibrary(true)} title={`Playlist Library (${figjamPlayer.library.length} saved)`} style={{ background: "rgba(232,212,77,0.12)", border: "1px solid rgba(232,212,77,0.3)", borderRadius: 8, padding: "6px 10px", color: "#E8D44D", cursor: "pointer", fontSize: 15, lineHeight: 1, position: "relative" }}>
                📚
                {figjamPlayer.library.length > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    background: "#FC4C02", color: "#fff",
                    fontSize: 9, fontWeight: 700, fontFamily: "monospace",
                    borderRadius: 8, padding: "1px 5px", minWidth: 14, textAlign: "center",
                    border: "1px solid #070b0f",
                  }}>
                    {figjamPlayer.library.length}
                  </span>
                )}
              </button>
              {/* Dev-only Spotify Player Test button — hidden in production
                  so it doesn't confuse friend testers. Restore by setting
                  process.env.NODE_ENV !== "production" to true if you want
                  it back on localhost. */}
              {process.env.NODE_ENV !== "production" && (
                <button onClick={() => setShowPlayerTest(true)} title="Spotify Player Test" style={{ background: "rgba(65,105,225,0.12)", border: "1px solid rgba(65,105,225,0.3)", borderRadius: 8, padding: "6px 10px", color: "#4169E1", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>
                  🧪
                </button>
              )}
              <button onClick={() => setShowPanel(true)} title="Connections" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 8, padding: "6px 10px", color: "rgba(255,255,255,0.55)", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>
                {anyConnected ? "🔗" : "⚙️"}
              </button>
            </div>
          </div>

          {/* Service filter tabs + Spotify sub-type toggles temporarily
              hidden — they're only useful once the multi-user timeline is
              wired up. See parked Option A/B in Phase D notes. */}
        </div>
      </div>

      {/* ── Timeline ── */}
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "18px 14px 0", position: "relative", zIndex: 1 }}>

        {/* Figjam in-app player tile — replaces Spotify Now Playing when active */}
        <FigjamPlayerTile player={figjamPlayer} />
        {/* Elevation graph — only visible when playing a playlist with ride data */}
        <ElevationGraph player={figjamPlayer} />
        {/* Resume last AI playlist (after refresh, before user hits play) */}
        <FigjamResumeTile player={figjamPlayer} />

        {/* Welcome / sign-in intro — shown to friend testers before they
            sign in. Replaces the legacy Connect Spotify/Strava buttons that
            pointed at the broken localhost auth URL. The Spotify sign-in
            here flows through Supabase (multi-user); Strava is connected
            from the Connections panel after sign-in. */}
        {!anyConnected && !anyLoading && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 28, marginBottom: 16 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display',serif", textAlign: "center", marginBottom: 8, color: "#fff" }}>
              Welcome to Figjam
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", textAlign: "center", marginBottom: 22, lineHeight: 1.55, fontStyle: "italic" }}>
              Experiential jams for your suffering
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 18, lineHeight: 1.7 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <span style={{ color: "#1DB954", fontWeight: 700, minWidth: 18 }}>1.</span>
                <span>Sign in with Spotify (Premium required to play music in-app).</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ color: "#FC4C02", fontWeight: 700, minWidth: 18 }}>2.</span>
                <span>Connect Strava from the ⚙️ Connections panel to pull your rides.</span>
              </div>
            </div>
            <button
              onClick={() => signInWithSpotify()}
              style={{ width: "100%", background: "#1DB954", border: "none", borderRadius: 10, padding: "13px 20px", color: "#0a0d11", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.01em", transition: "transform 0.15s, box-shadow 0.15s", boxShadow: "0 2px 12px rgba(29,185,84,0.25)" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(29,185,84,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(29,185,84,0.25)"; }}
            >
              Sign in with Spotify
            </button>
          </div>
        )}

        {/* Connect Strava prompt — shown when user has signed in via Supabase
            but hasn't connected Strava yet. The v2 status fetch determines
            whether the row exists in Postgres for this user. After OAuth
            round-trip the ?connected=strava query param triggers a refresh
            so this card disappears immediately. */}
        {supabaseSession && stravaV2Connected === false && (
          <div style={{ background: "rgba(252,76,2,0.06)", border: "1px solid rgba(252,76,2,0.25)", borderRadius: 16, padding: 22, marginBottom: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#FC4C02", fontFamily: "monospace", marginBottom: 6 }}>
              Step 2 of 2
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Playfair Display',serif", color: "#fff", marginBottom: 6 }}>
              Connect Strava
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", lineHeight: 1.55, marginBottom: 16 }}>
              Pull your rides + runs so Figjam can match the music to your terrain. You'll be redirected to Strava to authorize, then sent right back here.
            </div>
            <button
              onClick={startStravaConnect}
              style={{ width: "100%", background: "#FC4C02", border: "none", borderRadius: 10, padding: "13px 20px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.01em", transition: "transform 0.15s, box-shadow 0.15s", boxShadow: "0 2px 12px rgba(252,76,2,0.25)" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(252,76,2,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(252,76,2,0.25)"; }}
            >
              Connect Strava
            </button>
          </div>
        )}

        {filtered.length === 0 && !anyLoading && anyConnected && (
          <div style={{ textAlign: "center", padding: "50px 0", color: "rgba(255,255,255,0.2)" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
            <div style={{ fontSize: 13 }}>{search ? "Nothing matches your search" : "No items yet — try refreshing"}</div>
          </div>
        )}

        {grouped.map((entry, idx) => {
          // Helper to render a single item card
          const renderCard = (item, animIdx, opts = {}) => {
            const svc  = SVC[item.service] || SVC.spotify;
            const open = expanded === item.id;
            const plName = item.extra?.playlistName;
            const plUri = item.extra?.contextUri;
            const isNowPlaying = item.type === "Now Playing";
            const nested = opts.nested || false;
            const nestedColor = opts.nestedColor || svc.color;

            const cardColor = isNowPlaying ? "#4169E1" : svc.color;
            const cardBg = isNowPlaying ? "linear-gradient(135deg,#070b1a 0%,#0f1a3d 100%)" : svc.bg;

            // Playlist label logic (only for non-nested Spotify items)
            const prevItem = !nested && animIdx > 0 ? grouped[animIdx - 1]?.item : null;
            const prevPlUri = prevItem?.extra?.contextUri;
            const isFirstInPlaylist = !nested && plName && plUri !== prevPlUri;
            const isContinuation = !nested && plName && plUri && plUri === prevPlUri;
            const inPlaylist = isFirstInPlaylist || isContinuation;

            return (
              <div key={item.id} style={{ animation: nested ? "none" : `fadeUp 0.38s ease ${Math.min(animIdx, 15) * 0.04}s both` }}>
                {/* Now Playing label */}
                {isNowPlaying && !nested && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 2, paddingBottom: 2, paddingTop: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "#4169E1", fontFamily: "'Playfair Display',serif" }}>▶ Now Playing</span>
                    <div style={{ height: 1, flexGrow: 1, background: "linear-gradient(to right, #4169E140, transparent)" }} />
                  </div>
                )}

                {/* Playlist/album label */}
                {isNowPlaying && plName && !nested && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 18, paddingBottom: 4, paddingTop: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 600, color: "#E8D44D", fontFamily: "'Playfair Display',serif", opacity: 0.7 }}>
                      {item.extra.contextType === "album" ? "Album:" : item.extra.contextType === "artist" ? "Artist:" : "Playlist:"}
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "#1DB954", fontFamily: "'Playfair Display',serif" }}>{item.extra.playlistName}</span>
                  </div>
                )}
                {isFirstInPlaylist && !isNowPlaying && !nested && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 2, paddingBottom: 4, paddingTop: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 600, color: "#E8D44D", fontFamily: "'Playfair Display',serif", opacity: 0.7 }}>
                      {item.extra.contextType === "album" ? "Album:" : item.extra.contextType === "artist" ? "Artist:" : "Playlist:"}
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: svc.color, fontFamily: "'Playfair Display',serif" }}>{item.extra.playlistName}</span>
                  </div>
                )}

                {/* Card */}
                <div style={{ display: "flex", alignItems: "flex-start", marginLeft: nested ? 16 : (inPlaylist && !isNowPlaying ? 16 : 0) }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 34, flexShrink: 0 }}>
                    {isFirstInPlaylist && !isNowPlaying && !nested && <div style={{ width: 0, height: 8, borderLeft: `2px dashed ${svc.color}70` }} />}
                    {isContinuation && !isNowPlaying && !nested && <div style={{ width: 0, minHeight: 16, borderLeft: `2px dashed ${svc.color}70` }} />}
                    {nested && <div style={{ width: 0, minHeight: 10, borderLeft: `2px dashed ${nestedColor}50` }} />}
                    <div style={{ width: nested ? 8 : (isNowPlaying ? 13 : 11), height: nested ? 8 : (isNowPlaying ? 13 : 11), borderRadius: "50%", background: nested ? nestedColor : cardColor, boxShadow: `0 0 ${isNowPlaying ? 12 : 6}px ${(nested ? nestedColor : cardColor)}50`, zIndex: 1, marginTop: (inPlaylist || nested) && !isNowPlaying ? 0 : 19 }} />
                    <div style={{ width: 0, borderLeft: (inPlaylist || nested) && !isNowPlaying ? `2px dashed ${(nested ? nestedColor : svc.color)}50` : `2px solid ${cardColor}40`, flexGrow: 1, minHeight: 12 }} />
                  </div>
                  <div
                    onClick={() => setExpanded(open ? null : item.id)}
                    style={{ flex: 1, marginLeft: 10, marginBottom: (inPlaylist || nested) && !isNowPlaying ? 4 : 12, background: cardBg, border: `1px solid ${cardColor}22`, borderRadius: 13, padding: nested ? "10px 13px" : "14px 15px", cursor: "pointer", transition: "transform 0.16s,box-shadow 0.16s", boxShadow: open ? `0 8px 28px ${cardColor}28` : "0 2px 8px rgba(0,0,0,0.35)" }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", gap: 9, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: nested ? 14 : 18, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {!nested && (
                            <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: cardColor, fontFamily: "monospace" }}>{dateTime(item.timestamp)}</span>
                            </div>
                          )}
                          <TruncatedText
                            text={item.title}
                            tooltipColor={cardColor}
                            style={{ fontSize: nested ? 13 : 14, fontWeight: 700, color: "#fff", marginTop: nested ? 0 : 2, fontFamily: "'Playfair Display',serif" }}
                          />
                          <TruncatedText
                            text={item.subtitle}
                            tooltipColor={cardColor}
                            style={{ fontSize: nested ? 10 : 11, color: "rgba(255,255,255,0.65)", marginTop: 1 }}
                          />
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, marginLeft: 8, display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "monospace" }}>{ago(item.timestamp)}</span>
                        {item.extra?.spotifyUri && <SpotifyLink extra={item.extra} color={cardColor} />}
                        <span style={{ fontSize: 9, color: cardColor, opacity: 0.65 }}>{open ? "▲" : "▼"}</span>
                      </div>
                    </div>
                    {open && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${(nested ? nestedColor : svc.color)}18` }}>
                        <CardDetail item={item} color={nested ? nestedColor : svc.color} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          if (entry.kind === "single") {
            return renderCard(entry.item, idx);
          }

          // ── Strava activity with nested songs ──
          const { activity, songs } = entry;
          const stravaColor = SVC.strava.color;
          const actOpen = expanded === activity.id;
          return (
            <div key={activity.id} style={{ animation: `fadeUp 0.38s ease ${Math.min(idx, 15) * 0.04}s both` }}>
              {/* Strava activity card */}
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 34, flexShrink: 0 }}>
                  <div style={{ width: 13, height: 13, borderRadius: "50%", background: stravaColor, boxShadow: `0 0 10px ${stravaColor}70`, zIndex: 1, marginTop: 19 }} />
                  <div style={{ width: 0, borderLeft: songs.length > 0 ? `2px dashed ${stravaColor}50` : `2px solid ${stravaColor}40`, flexGrow: 1, minHeight: 12 }} />
                </div>
                <div
                  onClick={() => setExpanded(actOpen ? null : activity.id)}
                  style={{ flex: 1, marginLeft: 10, marginBottom: songs.length > 0 ? 4 : 12, background: SVC.strava.bg, border: `1px solid ${stravaColor}22`, borderRadius: 13, padding: "14px 15px", cursor: "pointer", transition: "transform 0.16s,box-shadow 0.16s", boxShadow: actOpen ? `0 8px 28px ${stravaColor}28` : "0 2px 8px rgba(0,0,0,0.35)" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", gap: 9, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{activity.icon}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: stravaColor, fontFamily: "monospace" }}>STRAVA</span>
                          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.28)", fontFamily: "monospace" }}>· {activity.type}</span>
                          {songs.length > 0 && <span style={{ fontSize: 9, color: "#1DB954", fontFamily: "monospace" }}>· 🎧 {songs.length} songs</span>}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 2, fontFamily: "'Playfair Display',serif" }}>{activity.title}</div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{activity.subtitle}</div>
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, marginLeft: 8, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "monospace" }}>{ago(activity.timestamp)}</div>
                      <span style={{ fontSize: 9, color: stravaColor, opacity: 0.65 }}>{actOpen ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {actOpen && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${stravaColor}18` }}>
                      <CardDetail item={activity} color={stravaColor} />
                    </div>
                  )}
                </div>
              </div>

              {/* Nested songs played during this activity */}
              {songs.map(song => renderCard(song, idx, { nested: true, nestedColor: stravaColor }))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
